import draftToHtml from "draftjs-to-html";
import { produce } from "immer";
import tinycolor from "tinycolor2";
import queryString from "query-string";
import React from "react";
import { Typography, Button } from "@mui/material"; // Adjust this import based on your UI library

const distanceAtEnds = {
  screen: {
    start: 1,
    end: 2,
  },
  cut: {
    start: 1,
    end: 1.2,
  },
  dribble: {
    start: 1,
    end: 1,
  },
  pass: {
    start: 1,
    end: 1,
  },
  shot: {
    start: 1,
    end: 0,
  },
};
const maxHandoffDistance = 10;
const defaultBackgroundColor = "rgba(6, 147, 227,0.5)";
const defaultColor = "rgba(51, 51, 51, 1)";
const defaultDefenseColor = "rgba(235, 20, 76,1)";

const Helper = {
  defaultColor: defaultColor,
  defaultDefenseColor: defaultDefenseColor,
  defaultBackgroundColor: defaultBackgroundColor,
  colors: [
    "rgb(51, 51, 51)",
    "rgb(153, 0, 239)",
    "rgb(63, 81, 181)",
    "rgb(6, 147, 227)",
    "rgb(0, 208, 132)",
    "rgb(0, 150, 136)",
    "rgb(252, 185, 0)",
    "rgb(255, 105, 0)",
    "rgb(235, 20, 76)",
    "rgb(238, 238, 238)",
    "rgb(171, 184, 195)",
  ],

  statusArray: [
    "SELECTED",
    "SELECTED_MULTIPLE",
    "INTERACTING",
    "EDIT",
    "EDIT_MOBILE",
    "NOT_SELECTED",
    "HOVERING",
    "HOVERING_ANIMATOR",
  ],
  mobileDragOffset: 35,
  playerLabels: [
    {
      id: 1,
      label: "1",
    },
    {
      id: 2,
      label: "2",
    },
    {
      id: 3,
      label: "3",
    },
    {
      id: 4,
      label: "4",
    },
    {
      id: 5,
      label: "5",
    },
    {
      id: 0,
      label: "?",
    },
  ],
  breakpoints: {
    desktop: 1100,
    tablet: 768,
  },
  modelVersion: 10,
  playbookModelVersion: 3,
  rotationModelVersion: 2,
  printSettingsModelVersion: 2,
  hoops: {
    NBA: [
      [0, 5.25],
      [0, 88.75],
    ],
    NCAA: [
      [0, 5.25],
      [0, 88.75],
    ],
    HighSchool: [
      [0, 5.25],
      [0, 78.75],
    ],
    FIBA: [
      [0, 5.25],
      [0, 86.61],
    ],
  },

  defaultConeObject: {
    type: "object",
    subType: "cone",
    team: null,
    pos: [0, 38],
    number: null,
    color: defaultColor,
    status: "SELECTED",
  },

  defaultTextObject: {
    type: "object",
    subType: "text",
    pos: [-10, 20],
    width: 20,
    height: 5,
    color: defaultColor,
    alignment: "center",
    verticalAlignment: "flex-start",
    formats: [],
    status: "EDIT",
    fontSize: 20,
    text: "",
    pause: 1,
    animationType: "end",
    backgroundColor: "transparent",
    angle: 0,
  },

  defaultRectangleObject: {
    type: "object",
    subType: "rectangle",
    pos: [-10, 20],
    width: 20,
    height: 10,
    status: "SELECTED",
    pause: 1,
    animationType: "end",
    backgroundColor: defaultBackgroundColor,
    angle: 0,
  },

  defaultCircleObject: {
    type: "object",
    subType: "circle",
    pos: [0, 20],
    rx: 8,
    ry: 8,
    status: "SELECTED",
    pause: 1,
    animationType: "end",
    backgroundColor: defaultBackgroundColor,
    angle: 0,
  },

  bunnyStatusMapping: {
    0: { label: "Processing", color: "warning" },
    1: { label: "Processing", color: "warning" },
    2: { label: "Processing", color: "warning" },
    3: { label: "Finished", color: "success" },
    4: { label: "Processing", color: "warning" },
    5: { label: "Upload Failed", color: "error" },
    6: { label: "Processing", color: "warning" },
    7: { label: "Processing", color: "warning" },
    8: { label: "Upload Failed", color: "error" },
    9: { label: "Processing", color: "warning" },
  },

  sendMessageToWebview: (message) => {
    try {
      window?.ReactNativeWebView?.postMessage(JSON.stringify(message));
    } catch (e) {
      console.log("error sending message to webview", e);
    }
  },

  getNextEvenNumber: (number) => {
    let floor = Math.floor(number);
    return floor % 2 === 0 ? floor : floor - 1;
  },

  calculateLayout: (width, height, court, animateNotes) => {
    if (animateNotes === "play") {
      return {
        play: {
          width: width,
          height: Helper.getNextEvenNumber(Helper.getHeight(width, court)),
        },
        notes: {
          width: 0,
          height: 0,
        },
        video: {
          width: width,
          height: Helper.getNextEvenNumber(Helper.getHeight(width, court)),
        },
      };
    } else if (animateNotes === "bottom") {
      return {
        play: {
          width: width,
          height: Helper.getNextEvenNumber(Helper.getHeight(width, court)),
        },
        notes: {
          width: width,
          height:
            height - Helper.getNextEvenNumber(Helper.getHeight(width, court)),
        },
        video: {
          width: width,
          height: height,
        },
      };
    } else if (animateNotes === "right") {
      return {
        play: {
          width: height / Helper.getHeight(1, court),
          height: height,
        },
        notes: {
          width: width - height / Helper.getHeight(1, court),
          height: height,
        },
        video: {
          width: width,
          height: height,
        },
      };
    }
  },

  isMobile: () => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(pointer: coarse)").matches;
  },
  getPlayLink: (code) => {
    return `${import.meta.env.VITE_PUBLIC_API}/play/${code}`;
  },

  getPlaybookLink: (code) => {
    return `${import.meta.env.VITE_PUBLIC_API}/playbook/${code}`;
  },
  getRotationLink: (code) => {
    return `${import.meta.env.VITE_PUBLIC_API}/rotation/${code}`;
  },

  socialLoginNameMapping: {
    "google-oauth2": "Google",
    auth0: "Username/Password",
    apple: "Apple",
  },

  formatCurrency: (currencyCode, amount) => {
    const formatter = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currencyCode ?? "USD",
    });

    try {
      return formatter.format(amount);
    } catch (e) {
      console.log("error formatting currency", e);
      return amount;
    }
  },

  firstLetterUpperCase: (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  },

  checkIfUrlValidYoutube: (url) => {
    const youtubeUrlRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\//;
    return youtubeUrlRegex.test(url);
  },

  extractYoutubeIdFromUrl: (url) => {
    const youtubeUrlRegex =
      /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(youtubeUrlRegex);
    if (match && match[4]) {
      return match[4];
    }
    return null;
  },

  getPlaybookContent: (playbook) => {
    if (!playbook) {
      return [];
    }
    let playbookContent = [];
    playbook.chapters.forEach((chapter, i) => {
      playbookContent.push({
        type: "chapterHeader",
        title: chapter.title,
        chapterIndex: i,
      });

      playbookContent.push(
        ...chapter.objects.map((object, j) => ({
          chapterIndex: i,
          objectIndex: j,
          ...object,
        }))
      );
    });
    return playbookContent;
  },

  defaultPolygonObject: (pos = [0, 20], amount = 3) => ({
    type: "object",
    subType: "polygon",
    points: Helper.createPoints(pos, 7, amount),
    status: "SELECTED",
    pause: 1,
    animationType: "end",
    backgroundColor: defaultBackgroundColor,
  }),

  defaultTriangleObject: (pos = [0, 20]) => ({
    type: "object",
    subType: "triangle",
    points: Helper.createPoints(pos, 7, 3),
    status: "SELECTED",
    pause: 1,
    animationType: "end",
    backgroundColor: defaultBackgroundColor,
  }),

  checkPhaseForErrors: (phase) => {
    let steps = Helper.calculateNextPhase(phase).steps;
    let errors = steps.filter((step) => step.error !== false);
    return errors;
  },

  blobToBase64: (blobUrl) => {
    return fetch(blobUrl)
      .then((res) => res.blob())
      .then((blob) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      });
  },

  downloadBlob: async (
    blobUrl,
    name = "file.txt",
    type = "download",
    enqueueSnackbar = () => {}
  ) => {
    const isExpo = Helper.isExpo();

    if (isExpo) {
      const base64 = await Helper.blobToBase64(blobUrl);

      Helper.sendMessageToWebview({
        type: "DOWNLOAD",
        blob: base64,
        title: name,
      });

      return;
    }
    if (type === "download") {
      // Create a link element
      const link = document.createElement("a");

      // Set link's href to point to the Blob URL
      link.href = blobUrl;
      link.download = name;

      // Append link to the body
      document.body.appendChild(link);

      // Dispatch click event on the link
      // This is necessary as link.click() does not work on the latest firefox
      link.dispatchEvent(
        new MouseEvent("click", {
          bubbles: true,
          cancelable: true,
          view: window,
        })
      );

      // Remove link from body
      document.body.removeChild(link);
    } else {
      var printPage = window.open(blobUrl);
      if (!printPage) {
        console.log("blobUrl", blobUrl);
        console.error("Popup blocked. Please allow popups and try again.");
        enqueueSnackbar("Popup blocked. Please allow popups and try again.", {
          variant: "error",
        });
        return;
      }
      printPage.onload = function () {
        setTimeout(() => {
          try {
            // Print for Safari browser
            printPage.execCommand("print", false, null);
          } catch {
            printPage.print();
          }
        }, 500);
      };
    }
  },

  hotjar: (event) => {
    if (window.hj) {
      window.hj("event", event);
    } else {
      console.log("hotjar not loaded");
    }
  },

  checkIfSelected: (status) => {
    return (
      status === "SELECTED_MULTIPLE" ||
      status === "SELECTED" ||
      status === "INTERACTING" ||
      status === "EDIT"
    );
  },
  checkIfOutOfBounds: (pos, court) => {
    if (
      pos[0] < -court.width / 2 ||
      pos[0] > court.width / 2 ||
      pos[1] < 0 ||
      pos[1] > court.length
    ) {
      return true;
    }
    return false;
  },

  getEffectiveStatus: (props) => {
    if (props.editable) {
      return props.status;
    } else if (props.animatable) {
      if (props.status === "HOVERING_ANIMATOR") {
        return "HOVERING_ANIMATOR";
      } else {
        return "NOT_SELECTED";
      }
    } else return "NOT_SELECTED";
  },

  //create x points spread evenly around a center
  createPoints: (center, radius, numPoints) => {
    let points = [];
    for (let i = 0; i < numPoints; i++) {
      let angle = (i * 2 * Math.PI) / numPoints - Math.PI / 2;
      let x = center[0] + radius * Math.cos(angle);
      let y = center[1] + radius * Math.sin(angle);
      points.push([x, y]);
    }
    return points;
  },

  hasAnimation: (play) => {
    let tempSteps = [];
    for (let phase of play?.data?.phases?.phases ?? []) {
      tempSteps.push(
        ...Helper.calculateNextPhase(phase, play?.data?.settings?.editor?.court)
          .steps
      );
    }
    return tempSteps.length > 0;
  },

  getKeepAspectRatioResizeVector: (vector, edge, width, height) => {
    let point;

    switch (edge) {
      case "bottom-right": {
        point = {
          x: vector.x,
          y: vector.y,
        };
        break;
      }

      case "top-left": {
        point = {
          x: vector.x,
          y: vector.y,
        };
        break;
      }
      case "top-right": {
        point = {
          x: vector.x + width,
          y: vector.y - height,
        };
        break;
      }
      case "bottom-left": {
        point = {
          x: vector.x - width,
          y: vector.y + height,
        };
        break;
      }
    }

    let invertedPointFactor = ["top-left", "bottom-left"].includes(edge)
      ? 1
      : -1;
    let ratioFactor = ["top-left", "bottom-right"].includes(edge) ? 1 : -1;

    if (
      invertedPointFactor *
        (point.y - point.x * ((height / width) * ratioFactor)) >
      0
    ) {
      return ratioFactor === 1
        ? {
            x: vector.x,
            y: (vector.x * height) / width,
          }
        : {
            x: -(vector.y * width) / height,
            y: vector.y,
          };
    } else {
      return ratioFactor === 1
        ? {
            x: (vector.y * width) / height,
            y: vector.y,
          }
        : {
            x: vector.x,
            y: -(vector.x * height) / width,
          };
    }
  },
  getAngleOfVector: (vector) => {
    return (Math.atan2(vector[1], vector[0]) * 180) / Math.PI;
  },

  getAngle: (point1, point2) => {
    let angle =
      (Math.atan2(point2.y - point1.y, point2.x - point1.x) * 180) / Math.PI;
    return angle;
  },

  getCenter: (posX, posY, width, height) => {
    return [posX + width / 2, posY + height / 2];
  },

  translateEdges: {
    top: "right",
    "top-right": "bottom-right",
    right: "bottom",
    "bottom-right": "bottom-left",
    bottom: "left",
    "bottom-left": "top-left",
    left: "top",
    "top-left": "top-right",
  },

  moveRect: (delta, edge, posX, posY, height, width) => {
    //let translatedEdge = fullCourt ? Helper.translateEdges[edge] : edge;
    //console.log("translatedE",translatedEdge);
    switch (edge) {
      case "top":
        return {
          x: posX,
          y: Math.min(posY + delta.y, posY + height),
          width,
          height: Math.max(0, height - delta.y),
        };
      case "top-right":
        return {
          x: posX,
          y: Math.min(posY + delta.y, posY + height),
          width: Math.max(0, width + delta.x),
          height: Math.max(0, height - delta.y),
        };
      case "right":
        return {
          x: posX,
          y: posY,
          width: Math.max(0, width + delta.x),
          height,
        };
      case "bottom-right":
        return {
          x: posX,
          y: posY,
          width: Math.max(0, width + delta.x),
          height: Math.max(0, height + delta.y),
        };
      case "bottom":
        return {
          x: posX,
          y: posY,
          width,
          height: Math.max(0, height + delta.y),
        };
      case "bottom-left":
        return {
          x: Math.min(posX + delta.x, posX + width),
          y: posY,
          width: Math.max(0, width - delta.x),
          height: Math.max(0, height + delta.y),
        };
      case "left":
        return {
          x: Math.min(posX + delta.x, posX + width),
          y: posY,
          height,
          width: Math.max(0, width - delta.x),
        };
      case "top-left":
        return {
          x: Math.min(posX + delta.x, posX + width),
          y: Math.min(posY + delta.y, posY + height),
          width: Math.max(0, width - delta.x),
          height: Math.max(0, height - delta.y),
        };
    }
  },

  drawCircle: (x, y, id, color = "red") => {
    let circle = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "circle"
    );
    circle.setAttribute("cx", x);
    circle.setAttribute("cy", y);
    circle.setAttribute("r", 1);
    circle.setAttribute("fill", color);
    circle.setAttribute("stroke-width", 0);
    document.getElementById(id).appendChild(circle);
  },

  checkForPermission: (user, right) => {
    return user?.permissions?.includes(right) ?? false;
  },

  linkStillValid: (link, objects, playerRadius) => {
    let linkPlayer = objects.find((x) => x.id === link.player.id);
    let linkObject = objects.find((x) => x.id === link.object.id);
    let linkPlayerPos = linkPlayer.pos ?? linkPlayer.e;
    let linkObjectPos = linkObject.s ?? linkObject.pos;

    /*
    console.log("link", JSON.parse(JSON.stringify(link)));
    console.log("linkPlayer", JSON.parse(JSON.stringify(linkPlayer)));
    console.log("linkObject", JSON.parse(JSON.stringify(linkObject)));
    console.log("linkPlayerPos", JSON.parse(JSON.stringify(linkPlayerPos)));
    console.log("linkObjectPos", JSON.parse(JSON.stringify(linkObjectPos)));
*/

    if (!linkPlayer || !linkObject) return false;

    if (link.object.subType === "handoff") {
      if (Helper.distance(linkPlayerPos, linkObjectPos) > maxHandoffDistance) {
        return false;
      }
    } else {
      if (link.point === "s") {
        if (Helper.distance(linkPlayerPos, linkObjectPos) > playerRadius) {
          return false;
        }
      } else if (link.point === "e") {
        if (Helper.distance(linkPlayerPos, linkObjectPos) > playerRadius) {
          return false;
        }
      }
    }
    return true;
  },
  isNotAlreadyLinked(action, links) {
    let ends = action.ends;
    if (!ends) {
      return ends;
    }

    let relevantLinks = links.filter((link) => link.object.id === action.id);

    relevantLinks.forEach((link) => {
      if (ends.includes(link.point)) {
        ends = ends.filter((end) => end !== link.point);
      }
    });

    return ends;
  },

  createLinksV2: (objects, oldLinks, playerRadius) => {
    let stillValidLinks = oldLinks.filter((link) =>
      Helper.linkStillValid(link, objects, playerRadius)
    );

    let actions = objects.filter(
      (x) => x.type === "arrow" || x.subType === "handoff"
    );

    //for each array in actions create two arrays (one with end:"e" and one with end"s")
    let actionsWithEnds = actions.map((action) => ({
      ...action,
      ends: ["s", "e"],
    }));

    actionsWithEnds = actionsWithEnds.map((action) => ({
      ...action,
      ends: Helper.isNotAlreadyLinked(action, stillValidLinks),
    }));

    let others = objects.filter(
      (x) => x.type !== "arrow" && x.subType !== "handoff"
    );
    let links = [];

    actionsWithEnds.forEach((action, index) => {
      let hoops = objects.filter((x) => x.subType === "hoop");
      let futureplayers = Helper.getFuturePlayers([
        ...others,
        ...actions.slice(0, index),
      ]);

      let allPlayers = [...hoops, ...futureplayers];

      if (action.subType === "handoff") {
        let handoffLinks = Helper.findHandOffLinks(
          action.pos,
          allPlayers,
          action.ends
        );
        links = [
          ...links,
          ...handoffLinks.map((link) => ({ ...link, object: action })),
        ];
      } else {
        let snapResult = Helper.snapActionToPlayer(
          action,
          allPlayers,
          playerRadius
        );

        //find corresponding index  to this

        let objectIndex = objects.findIndex((x) => x.id === action.id);

        objects[objectIndex] = {
          ...objects[objectIndex],
          ...snapResult.points,
        };

        let newAction = {
          ...action,
          ...snapResult.points,
        };
        links = [
          ...links,
          ...snapResult.links.map((link) => ({ ...link, object: newAction })),
        ];
      }
    });

    let newLinks = links.map((link) => ({
      ...link,
      object: {
        id: link.object.id,
        subType: link.object.subType,
        type: link.object.type,
      },
      player: {
        id: link.player.id,
        subType: link.player.subType,
        type: link.player.type,
      },
    }));

    return { links: [...stillValidLinks, ...newLinks], objects, newLinks };
  },

  convertRectFromSVG: (x, y, width, height, full) => {
    if (!full) {
      return { x, y, width, height };
    } else {
      return {
        x: -y - height,
        y: x,
        width: height,
        height: width,
      };
    }
  },

  convertRectToSVG: (x, y, width, height, full) => {
    if (!full) {
      return { x, y, width, height };
    } else {
      return {
        x: x,
        y: y - width,
        width: height,
        height: width,
      };
    }
  },

  convertEllipseToSVG: (x, y, rx, ry, full) => {
    if (!full) {
      return { x, y, rx, ry };
    } else {
      return {
        x: x,
        y: y,
        rx: ry,
        ry: rx,
      };
    }
  },

  convertEllipseFromSVG: (x, y, rx, ry, full) => {
    if (!full) {
      return { x, y, rx, ry };
    } else {
      return {
        x: -y,
        y: x,
        rx: ry,
        ry: rx,
      };
    }
  },

  isExpo: () => {
    return (
      typeof navigator !== "undefined" && navigator.userAgent.includes("Expo")
    );
  },
  getEnvironment: () => ({
    app: "web",
    isPWA: window.matchMedia("(display-mode: standalone)").matches ?? false,
    isExpo: false,
    commitHash: __COMMIT_HASH__, //eslint-disable-line
    ...(window.thg_environment ?? {}),
  }),

  inititalPositionElement: (elementNode, event) => {
    let { width, height } = elementNode.getBoundingClientRect();
    let offset =
      elementNode.id === "drag_arrow" || elementNode.id === "drag_arrow_mobile"
        ? 60
        : 0;
    elementNode.setAttribute("data-x", 0);
    elementNode.setAttribute("data-y", 0);
    elementNode.style.left = event.clientX - (width - offset) / 2 + "px";
    elementNode.style.top = event.clientY - height / 2 + "px";
    elementNode.style.visibility = "visible";
    elementNode.style.position = "fixed";
  },

  getAlphaRGB: (hex, opacity) => {
    if (!hex) return "transparent";
    // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
    var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, function (m, r, g, b) {
      return r + r + g + g + b + b;
    });
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return "transparent";
    let rgb = {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16),
    };
    return `rgba(${rgb.r},${rgb.g},${rgb.b},${opacity / 100})`;
  },

  getPhaseTitle: (activePhase, phases, type = "output") => {
    let currentTitle = activePhase?.title;
    let defaultTitle =
      "Phase " + Helper.getCurrentPhaseNumber(phases, activePhase.id);
    let outputTitle =
      currentTitle && currentTitle !== "" ? currentTitle : defaultTitle;

    switch (type) {
      case "default": {
        return defaultTitle;
      }
      case "output": {
        return outputTitle;
      }
      case "input": {
        return currentTitle;
      }
      default: {
        return outputTitle;
      }
    }
  },

  getPhaseTitlePerformant: (activePhase, index, type = "output") => {
    let currentTitle = activePhase.title;
    let defaultTitle = "Phase " + (index + 1);
    let outputTitle =
      currentTitle && currentTitle !== "" ? currentTitle : defaultTitle;

    switch (type) {
      case "default": {
        return defaultTitle;
      }
      case "output": {
        return outputTitle;
      }
      case "input": {
        return currentTitle;
      }
      default: {
        return outputTitle;
      }
    }
  },

  getMaxCoordinates: (court) => {
    let minX = court.full
      ? court.sidelines * -1
      : (court.width / 2 + court.sidelines) * -1;

    let minY = court.full
      ? (court.width / 2 + court.sidelines) * -1
      : court.sidelines * -1;

    let viewBoxWidth = court.full
      ? court.length + 2 * court.sidelines
      : court.width + 2 * court.sidelines;

    let viewBoxHeight = court.full
      ? court.width + 2 * court.sidelines
      : (court.length + 2 * court.sidelines) / 2;

    let maxX = minX + viewBoxWidth;
    let maxY = minY + viewBoxHeight;

    return { minX, minY, maxX, maxY, viewBoxWidth, viewBoxHeight };
  },

  objectsOutOfView: (court, items) => {
    let outOfView = items.filter((item) => {
      return Helper.isItemOutOfView(court, item);
    });
    return outOfView;
  },

  isArrayEqualToNullArray: (arr) => {
    return (
      Array.isArray(arr) &&
      arr.length === 2 &&
      arr[0] === null &&
      arr[1] === null
    );
  },

  invalidItems: (items) => {
    let keys = ["s", "m", "e", "h1", "h2"];
    //check if any value in the key has [null,null]
    return items.filter((item) => {
      return keys.some((key) => {
        return Helper.isArrayEqualToNullArray(item[key]);
      });
    });
  },

  circleIsNotIntersectingWithCourt: (minX, minY, maxX, maxY, pos, rx, ry) => {
    const centerX = pos[0];
    const centerY = pos[1];

    // Check if the ellipse is entirely outside the rectangle
    if (
      centerX + rx < minX ||
      centerX - rx > maxX ||
      centerY + ry < minY ||
      centerY - ry > maxY
    ) {
      return true; // The ellipse is not inside or intersecting with the rectangle
    }

    // Check if the ellipse is entirely inside the rectangle
    if (
      centerX - rx >= minX &&
      centerX + rx <= maxX &&
      centerY - ry >= minY &&
      centerY + ry <= maxY
    ) {
      return false; // The ellipse is entirely inside the rectangle
    }

    // Check if the ellipse intersects with the rectangle
    const closestX = Math.max(minX, Math.min(centerX, maxX));
    const closestY = Math.max(minY, Math.min(centerY, maxY));
    const distanceX = centerX - closestX;
    const distanceY = centerY - closestY;
    return (
      (distanceX * distanceX) / (rx * rx) +
        (distanceY * distanceY) / (ry * ry) >=
      1
    );
  },

  isItemOutOfView: (court, item) => {
    if (item.subType === "hoop") {
      return false;
    }

    let { minX, minY, maxX, maxY } = Helper.getMaxCoordinates(court);
    if (item.subType === "text" || item.subType === "rectangle") {
      let pos = Helper.transposeCoord(item.pos, court);
      return (
        pos[0] + item.width < minX ||
        pos[0] > maxX ||
        pos[1] + item.height < minY ||
        pos[1] > maxY
      );
    } else if (item.subType === "circle") {
      let pos = Helper.transposeCoord(item.pos, court);
      return Helper.circleIsNotIntersectingWithCourt(
        minX,
        minY,
        maxX,
        maxY,
        pos,
        item.rx,
        item.ry
      );
    } else if (["cone", "player"].includes(item.subType)) {
      let pos = Helper.transposeCoord(item.pos, court);
      return pos[0] < minX || pos[0] > maxX || pos[1] < minY || pos[1] > maxY;
    } else if (item.type === "arrow") {
      let objProps = ["s", "m", "e", "h1", "h2"];
      let returnValue = false;
      objProps.forEach((prop) => {
        if (!item[prop]) {
          return false;
        }
        let pos = Helper.transposeCoord(item[prop], court);
        if (pos[0] < minX || pos[0] > maxX || pos[1] < minY || pos[1] > maxY) {
          returnValue = true;
        }
      });

      return returnValue;
    } else if (["triangle", "polygon"].includes(item.subType)) {
      let returnValue = true;
      item.points.forEach((point) => {
        let pos = Helper.transposeCoord(point, court);
        if (
          !(pos[0] < minX || pos[0] > maxX || pos[1] < minY || pos[1] > maxY)
        ) {
          returnValue = false;
        }
      });
      return returnValue;
    }
  },

  cleanData: (phases) => {
    return phases.map((phase) => ({
      ...phase,
      objects: phase.objects.map((object) => ({
        ...object,
        status: "NOT_SELECTED",
      })),
    }));
  },

  cleanSettingsForSave: (settings) => {
    let cleanedSettings = produce(settings, (draftSettings) => {
      delete draftSettings.play_id;
      delete draftSettings.printSettings;
      delete draftSettings.showOverlay;
      delete draftSettings.tabIndex;
      delete draftSettings.tags;
    });
    return cleanedSettings;
  },
  printError: (error) => {
    return (
      error?.response?.data?.error ??
      error?.response?.data?.message ??
      error.message
    );
  },

  convertDraftToQuill: (rawContent) => {
    let html = draftToHtml(rawContent);
    return html;
  },
  getDateFromMysql: (date) => {
    try {
      let arr = date.split(/-|\s|:/);
      let newDate = new Date(
        Date.UTC(arr[0], arr[1] - 1, arr[2], arr[3], arr[4], arr[5])
      );
      return newDate;
    } catch (e) {
      return null;
    }
  },

  extractYoutubeID: (url) => {
    try {
      var tempURL = new URL(url);
    } catch (error) {
      return null;
    }
    if (!tempURL) {
      return null;
    }
    if (tempURL.host === "www.youtube.com") {
      let parsedURL = queryString.parse(tempURL.search);
      return parsedURL.v;
    } else if (tempURL.host === "youtu.be") {
      return tempURL.pathname.split("/")[1];
    }
    return false;
  },

  gaEvent: (category, action, label) => {
    //console.log(category,action,label);
    if (typeof window.ga === "function") {
      window.ga("send", "event", category, action, label);
    } else {
      console.log("tracking error: ga not a function", category, action, label);
    }
  },

  createPlaceholder: (amount, id = "id") => {
    let data = [];
    for (let i = 0; i < amount; i++) {
      data.push({ [id]: i, placeholder: true });
    }
    return data;
  },

  convertToLatestPlaybookDataModel: (data) => {
    let modelVersion = Number(data.data?.settings?.playbookModelVersion ?? 1);
    let tempData = data.data;
    for (let i = modelVersion + 1; i <= Helper.playbookModelVersion; i++) {
      tempData = Helper.playbookMigrations[i](tempData);
    }

    if (!tempData.settings.cover) {
      tempData = {
        ...tempData,
        settings: {
          ...tempData.settings,
          cover: Helper.defaultCoverSettings,
        },
      };
    }

    return {
      ...data,
      data: tempData,
    };
  },

  playbookMigrations: {
    2: (state) => {
      return {
        chapters: state,
        settings: {
          playbookModelVersion: 2,
        },
      };
    },
    3: (state) => {
      return {
        ...state,
        settings: {
          ...state.settings,
          playbookModelVersion: 3,
          cover: Helper.defaultCoverSettings,
        },
      };
    },
  },

  printSettingsMigrations: {
    2: (state) => {
      return {
        play: {
          ...state.play,
          objectSizeFactor: 1,
          overwriteScaling: false,
        },
        playbook: {
          ...state.playbook,
          objectSizeFactor: 1,
          overwriteScaling: false,
        },
      };
    },
    3: (state) => {
      return {
        ...state,
        rotation: Helper.defaultRotationPrintSettings,
      };
    },
  },

  convertToLatestDataModel: (data) => {
    let dataVersion = Number(data.data.settings.version);
    let tempData = {
      ...data.data,
      phases: {
        present: data.data.phases,
      },
    };
    for (let i = dataVersion + 1; i <= Helper.modelVersion; i++) {
      tempData = Helper.migrations[i](tempData);
    }
    return {
      ...tempData,
      phases: {
        ...tempData.phases.present,
      },
      settings: {
        ...tempData.settings,
        tabIndex: 0,
        version: Helper.modelVersion,
      },
    };
  },

  subTypePriorities: {
    rectangle: 0,
    polygon: 0,
    triangle: 0,
    circle: 0,
    hoop: 0,
    text: 1,
    cone: 1,
    cut: 2,
    screen: 2,
    pass: 2,
    dribble: 2,
    shot: 2,
    handoff: 2,
    player: 3,
  },

  reOrderAndAddObject: (objects, newObject) => {
    let reOrderedOldObjects = objects.map((object) => ({
      ...object,
      status: "NOT_SELECTED",
      displayOrder:
        object.displayOrder +
        (object.displayOrder >= newObject.displayOrder ? 1 : 0),
    }));

    if (Helper.subTypePriorities[newObject.subType] <= 1) {
      objects = [newObject, ...reOrderedOldObjects];
    } else {
      objects = [...reOrderedOldObjects, newObject];
    }

    return objects;
  },

  nextObjectDisplayOrder: (newObject, objects) => {
    let unchangedObjects = objects.filter(
      (x) => x.changedDisplayOrder !== true
    );

    unchangedObjects.sort((a, b) => a.displayOrder - b.displayOrder);

    let newIndex = unchangedObjects.findIndex(
      (x) =>
        Helper.subTypePriorities[x.subType] >
        Helper.subTypePriorities[newObject.subType]
    );

    if (newIndex === -1) {
      newIndex = unchangedObjects.length;
    }

    return newIndex;
  },
  addInitialOrdering: (objects) => {
    let tempObjects = objects.map((object, index) => ({
      id: object.id,
      subType: object.subType,
      animationOrder: index,
    }));

    tempObjects.sort((a, b) => {
      if (
        Helper.subTypePriorities[a.subType] >
        Helper.subTypePriorities[b.subType]
      ) {
        return 1;
      } else if (
        Helper.subTypePriorities[a.subType] <
        Helper.subTypePriorities[b.subType]
      ) {
        return -1;
      } else {
        return a.animationOrder - b.animationOrder;
      }
    });

    tempObjects = tempObjects.map((object, index) => ({
      ...object,
      displayOrder: index,
    }));

    return objects.map((object) => {
      let tempObject = tempObjects.find((tempObject) => {
        return tempObject.id === object.id;
      });
      return {
        ...object,
        displayOrder: tempObject.displayOrder,
      };
    });
  },

  migrations: {
    2: (state) => {
      return {
        ...state,
        phases: {
          ...state.phases,
          present: {
            ...state.phases.present,
            phases: state.phases.present.phases.map((phase) => ({
              ...phase,
              objects: phase.objects.map((object) => {
                let tempObject = { ...object };
                if (object.type === "player") {
                  tempObject.type = "object";
                  tempObject.subType = "player";
                } else if (object.type === "arrow") {
                  tempObject.type = "arrow";
                  tempObject.subType = object.arrowType;
                }
                return tempObject;
              }),
            })),
          },
        },
      };
    },
    3: (state) => {
      return {
        ...state,
        settings: {
          ...state.settings,
          courtColor: Helper.courtColors.filter(
            (x) => x.id === state.settings.courtColor.id
          )[0],
        },
      };
    },
    4: (state) => {
      return {
        ...state,
        phases: {
          ...state.phases,
          present: {
            ...state.phases.present,
            phases: state.phases.present.phases.map((phase) => ({
              ...phase,
              objects: phase.objects.map((object) => {
                let tempObject = { ...object };
                if (tempObject.type === "arrow") {
                  tempObject = {
                    ...tempObject,
                    shape: object.shape ?? 1,
                    h1: object.h1 ?? undefined,
                    h2: object.h2 ?? undefined,
                  };
                }
                return tempObject;
              }),
            })),
          },
        },
      };
    },
    5: (state) => {
      return {
        ...state,
        phases: {
          ...state.phases,
          present: {
            ...state.phases.present,
            phases: state.phases.present.phases.map((phase) => ({
              ...phase,
              links: phase.links ?? [],
            })),
          },
        },
      };
    },
    6: (state) => {
      return {
        ...state,
        settings: {
          ...state.settings,
          description: Helper.convertDraftToQuill(state.settings.description),
        },
        phases: {
          ...state.phases,
          present: {
            ...state.phases.present,
            phases: state.phases.present.phases.map((phase) => ({
              ...phase,
              note: Helper.convertDraftToQuill(phase.note),
            })),
          },
        },
      };
    },
    7: (state) => {
      return {
        ...state,
        phases: {
          ...state.phases,
          present: {
            ...state.phases.present,
            phases: state.phases.present.phases.map((phase) => ({
              ...phase,
              objects: Helper.addInitialOrdering(
                phase.objects
                  .filter((x) => x.subType !== undefined)
                  .map((object) => {
                    let tempresult = {
                      ...object,
                      subType: object.subType.toLowerCase(),
                    };
                    if (
                      ["text", "rectangle", "circle"].includes(
                        tempresult.subType
                      )
                    ) {
                      tempresult.angle = tempresult.angle ?? 0;
                    }
                    return Helper.roundObjectProperties(tempresult);
                  })
              ),
            })),
          },
        },
      };
    },
    8: (state) => {
      return {
        ...state,
        settings: {
          ...state.settings,
          objectSizeFactor: 1,
        },
      };
    },
    9: (state) => {
      return {
        ...state,
        phases: {
          ...state.phases,
          present: {
            ...state.phases.present,
            phases: state.phases.present.phases.map((phase) => ({
              ...phase,
              links: phase.links.map((link) => ({
                ...link,
                object: {
                  id: link.object.id,
                  subType: link.object.subType,
                  type: link.object.type,
                },
                player: {
                  id: link.player.id,
                  subType: link.player.subType,
                  type: link.player.type,
                },
              })),
              objects: phase.objects.map((object) => {
                let tempObject = { ...object };
                let backgroundOpacity = object.backgroundOpacity;
                delete tempObject.backgroundOpacity;
                if (
                  tempObject.backgroundColor === "transparent" ||
                  tempObject.backgroundColor === "" ||
                  tempObject.backgroundColor === undefined
                ) {
                  tempObject.backgroundColor = "transparent";
                  return tempObject;
                }
                if (tempObject.backgroundColor) {
                  let color = tinycolor(tempObject.backgroundColor);
                  if (color.isValid()) {
                    color.setAlpha(backgroundOpacity / 100);
                    let rgbString = color.toRgbString();

                    tempObject = {
                      ...tempObject,
                      backgroundColor: rgbString,
                    };
                  }
                }

                return tempObject;
              }),
            })),
          },
        },
      };
    },
    10: (state) => {
      let videos = [];
      if (
        state.settings.youtubeUrl !== undefined &&
        state.settings.youtubeUrl !== ""
      ) {
        videos.push({
          url: state.settings.youtubeUrl,
          source: "youtube",
          note: "",
        });
        state.settings.videos = videos;
      }
      delete state.settings.youtubeUrl;

      return state;
    },
  },
  gaEvent: (category, action, label) => {
    //console.log(category,action,label);
    try {
      if (typeof window !== "undefined" && typeof window?.ga === "function") {
        window.ga("send", "event", category, action, label);
      } else {
        console.log("tracking error: ga not a function");
        console.log("category", category, "action", action, "label", label);
      }
    } catch (error) {
      console.log(error);
    }
  },
  ga4Event: (event, props = {}) => {
    //using dataLayer
    try {
      window.dataLayer.push({
        event: event,
        ...props,
      });
    } catch (error) {
      console.log(error);
    }
  },

  courtColors: [
    {
      id: 0,
      showTiles: true,
      lineColor: "#fff",
      backgroundColor: "rgb(219,192,151)",
    },
    {
      id: 1,
      showTiles: false,
      backgroundColor: "rgb(239,204,154)",
      lineColor: "#fff",
    },
    {
      id: 2,
      showTiles: false,
      backgroundColor: "lightgrey",
      lineColor: "#fff",
    },
    {
      id: 3,
      showTiles: false,
      backgroundColor: "white",
      lineColor: "lightgrey",
    },
  ],
  defaultPlayPrintSettings: {
    fontSizes: { objectTitle: 20, play: 12, phase: 10, phaseTitle: 8 },
    courtColor: {
      id: 0,
      showTiles: true,
      lineColor: "#fff",
      backgroundColor: "rgb(219,192,151)",
    },
    cols: 2,
    pagePadding: {
      horizontal: 0.75,
      vertical: 0.75,
    },
    printPlayDescriptions: true,
    printPhaseDescriptions: true,
    printPhaseTitles: true,
    objectSizeFactor: 1,
    overwriteScaling: false,
    showLink: true,
  },

  defaultCoverSettings: {
    showCoverImage: true,
    showCoverPage: true,
    subtitle: null,
    image: null,
    imageWidth: 100,
    imageHeight: 50,
    titleFontSize: 35,
    titleMarginTop: 20,
    subtitleFontSize: 20,
    subtitleMarginTop: 10,
    verticalAlignment: "flex-start",
  },
  defaultPlaybookPrintSettings: {
    fontSizes: {
      title: 30,
      chapter: 25,
      objectTitle: 20,
      play: 12,
      phase: 8,
      phaseTitle: 8,
    },
    courtColor: {
      id: 0,
      showTiles: true,
      lineColor: "#fff",
      backgroundColor: "rgb(219,192,151)",
    },
    cols: 2,
    pageBreakAfterPlay: true,
    pagePadding: {
      horizontal: 0.75,
      vertical: 0.75,
    },
    printPlayDescriptions: true,
    printPhaseDescriptions: true,
    printPhaseTitles: true,
    objectSizeFactor: 1,
    overwriteScaling: false,
  },

  defaultRotationPrintSettings: {
    fontSizes: { title: 30, subheader: 16, description: 12 },
    printDescription: true,
    printSubstitutionChart: true,
    printPlayingTimeTable: true,
    printSubstitutionSheet: true,
    pagePadding: {
      horizontal: 0.75,
      vertical: 0.75,
    },
  },

  anglePerEdge: {
    top: 0,
    "top-right": 45,
    right: 90,
    "bottom-right": 135,
    bottom: 180,
    "bottom-left": 225,
    left: 270,
    "top-left": 315,
  },

  getMiddlePoint: (x, y) => {
    return [(x[0] + y[0]) / 2, (x[1] + y[1]) / 2];
  },
  normalizeVector: (x) => {
    //if 0/0 return [0,1]
    if (x[0] === 0 && x[1] === 0) {
      return [0, 1];
    }
    return [
      x[0] / Math.sqrt(Math.pow(x[0], 2) + Math.pow(x[1], 2)),
      x[1] / Math.sqrt(Math.pow(x[0], 2) + Math.pow(x[1], 2)),
    ];
  },
  roundCoords: (coords, precision = 2) => {
    if (!coords) return coords;
    return coords.map((x) => {
      return Helper.roundNumber(x, precision);
    });
  },
  roundNumber: (number, precision = 2) => {
    return (
      Math.round(number * Math.pow(10, precision)) / Math.pow(10, precision)
    );
  },
  roundObjectProperties: (changesObject, precision = 2) => {
    let coordKeys = ["pos", "s", "m", "e", "h1", "h2"];
    let numberKeys = ["width", "height", "rx", "ry"];

    let nextChangesObject = produce(changesObject, (draft) => {
      coordKeys.forEach((key) => {
        if (draft[key] !== undefined) {
          draft[key] = Helper.roundCoords(draft[key], precision);
        }
      });
      numberKeys.forEach((key) => {
        if (draft[key] !== undefined) {
          draft[key] = Helper.roundNumber(draft[key], precision);
        }
      });
      if (draft.points) {
        draft.points = draft.points.map((x) =>
          Helper.roundCoords(x, precision)
        );
      }
    });
    return nextChangesObject;
  },

  resetDisplayOrder: (objects) => {
    // all elemtents in the array objects have a property displayOrder. After deleting an object, the displayorder needs to be reset
    // go through all objects and set displayorder as the nth highest dispalyorder
    // if the displayorder is already set, it will be overwritten

    let nextObjects = produce(objects, (draft) => {
      draft.forEach((x, i) => {
        let nextDisplayOrder = objects.filter(
          (y) => y.displayOrder < x.displayOrder
        ).length;
        x.displayOrder = nextDisplayOrder;
      });
    });
    return nextObjects;

    /*
    return objects;
    let tempObjects = [...objects];
    tempObjects.sort((a, b) => a.displayOrder - b.displayOrder);
    tempObjects = tempObjects.map((x, i) => ({
      ...x,
      displayOrder: i,
    }));
    return tempObjects;*/
  },

  reOrderObjects: (objects, action) => {
    let { source, destination } = action;
    let addDiff = source > destination ? -0.5 : 0.5;
    let tempObjects = objects.map((x) => ({
      ...x,
      displayOrder:
        x.displayOrder === source ? destination + addDiff : x.displayOrder,
      changedDisplayOrder: x.displayOrder === source ? true : false,
    }));

    tempObjects.sort((a, b) => a.displayOrder - b.displayOrder);
    tempObjects = tempObjects.map((x, i) => ({
      ...x,
      nextDisplayOrder: i,
    }));

    return objects.map((x) => ({
      ...x,
      displayOrder: tempObjects.find((y) => y.id === x.id).nextDisplayOrder,
      changedDisplayOrder: tempObjects.find((y) => y.id === x.id)
        .changedDisplayOrder,
    }));
  },

  transposeCoord: (
    point,
    court = { full: false },
    direction = "CourtToSvg"
  ) => {
    if (point === undefined) {
      return undefined;
    }
    if (direction === "SvgToCourt") {
      if (court.full) {
        return [-point[1], point[0]];
      } else {
        return point;
      }
    } else {
      if (court.full) {
        return [point[1], -point[0]];
      } else {
        return point;
      }
    }
  },
  transformObjects: (objects, court) =>
    objects.map((item) => {
      if (item.type === "arrow") {
        return {
          ...item,
          s: Helper.transposeCoord(item.s, court),
          m: Helper.transposeCoord(item.m, court),
          e: Helper.transposeCoord(item.e, court),
          h1: Helper.transposeCoord(item.h1, court),
          h2: Helper.transposeCoord(item.h2, court),
        };
      } else if (item.type === "object") {
        return {
          ...item,
          points: item.points
            ? item.points.map((point) => Helper.transposeCoord(point, court))
            : undefined,

          pos: Helper.transposeCoord(item.pos, court),
        };
      }
      return null;
    }),
  getScalingFactor: (svgWidth, court) => {
    let courtWidth = court.full
      ? court.length + 2 * court.sidelines
      : court.width + 2 * court.sidelines;
    return Math.min(Math.max(svgWidth / courtWidth, 6), 12);
  },
  getPositionTitle: (team, number) => {
    let positions = {
      1: "Point Guard",
      2: "Shooting Guard",
      3: "Small Forward",
      4: "Power Forward",
      5: "Center",
    };
    let teamString = {
      ball: " with Ball",
      offense: " on Offense",
      defense: " on Defense",
    };
    return positions[number] + teamString[team];
  },

  findClosestPlayer: (coords, players, max = 10) => {
    let distanceArray = players.map((player) => {
      return { ...player, distance: Helper.distance(player.pos, coords) };
    });
    let closestPlayerArray = distanceArray
      .sort((a, b) => a.distance - b.distance)
      .filter((x) => x.distance <= max);
    if (closestPlayerArray[0]) {
      return players.filter((x) => x.id === closestPlayerArray[0].id)[0];
    } else {
      return null;
    }
  },
  getCurrentPhaseFromState: (state) => {
    let phaseIndex = state.phases.phases.findIndex(
      (x) => x.id === state.phases.activePhaseId
    );
    return state.phases.phases[phaseIndex];
  },

  addRotationToLinkedObjects: (afterLinking, newObject, currentPhase) => {
    let newStartLink = afterLinking.newLinks.find(
      (link) => link.object.type === "arrow" && link.point === "s"
    );

    if (newStartLink) {
      let affectedPlayer = currentPhase.objects.find(
        (object) => object.id === newStartLink.player.id
      );

      if (affectedPlayer.angle) {
        let affectedObject = currentPhase.objects.find(
          (object) => object.id === newObject.id
        );
        affectedObject.angle = affectedPlayer.angle;
      }
    }
  },

  addColorToLinkedObjects: (afterLinking, newObject, currentPhase) => {
    let newStartLink = afterLinking.newLinks.find(
      (link) => link.object.type === "arrow" && link.point === "s"
    );

    if (newStartLink) {
      let affectedPlayer = currentPhase.objects.find(
        (object) => object.id === newStartLink.player.id
      );

      if (affectedPlayer.color) {
        let affectedObject = currentPhase.objects.find(
          (object) => object.id === newObject.id
        );

        affectedObject.color = affectedPlayer.color;
      }
    }
  },

  getCurrentPhase: (phases, phaseId) => {
    let phaseIndex = phases.findIndex((x) => x.id === phaseId);
    return phases[phaseIndex];
  },
  getPreviousPhase: (phases, phaseId) => {
    let phaseIndex = phases.findIndex((x) => x.id === phaseId);
    return phases[phaseIndex - 1];
  },
  getNextPhase: (phases, phaseId) => {
    let phaseIndex = phases.findIndex((x) => x.id === phaseId);
    return phases[phaseIndex + 1];
  },

  getCurrentPhaseNumber: (phases, phaseId) => {
    let phaseIndex = phases.findIndex((x) => x.id === phaseId);
    return phaseIndex + 1;
  },
  nextObjectId: (objects) => {
    let maxObjectNumber =
      objects.length === 0
        ? 1
        : Math.max(...objects.map((x) => Number(x.id.split("_")[1])));
    return "object_" + (maxObjectNumber + 1);
  },
  replaceObjectIds: (oldObjects, newObjects) => {
    let maxObjectNumber =
      oldObjects.length === 0
        ? 1
        : Math.max(...oldObjects.map((x) => Number(x.id.split("_")[1])));

    let nextObjects = newObjects.map((x, i) => ({
      ...x,
      id: "object_" + (maxObjectNumber + i + 1),
    }));

    let mapping = newObjects.map((x, i) => ({
      oldId: x.id,
      newId: "object_" + (maxObjectNumber + i + 1),
    }));

    return {
      objects: nextObjects,
      mapping: mapping,
    };
  },

  selectedObject: (objects) => {
    return (
      objects.filter(
        (x) =>
          x.status === "SELECTED" ||
          x.status === "SELECTED_MULTIPLE" ||
          x.status === "INTERACTING"
      )[0] ?? false
    );
  },
  cleanLinks: (links, objects) => {
    return links.filter(
      (link) =>
        objects.findIndex((object) => object.id === link.object.id) !== -1 &&
        objects.findIndex((object) => object.id === link.player.id) !== -1
    );
  },
  getFuturePlayers: (objects) => {
    let players = objects.filter((x) => x.subType === "player");
    let arrowEnds = objects
      .filter(
        (x) =>
          x.type === "arrow" && ["cut", "screen", "dribble"].includes(x.subType)
      )
      .map((x) => ({
        ...x,
        pos: x.e,
        team: ["screen", "cut"].includes(x.subType) ? "offense" : "ball",
      }));

    let passEnds = objects.filter(
      (x) => x.type === "arrow" && x.subType === "pass"
    );

    let passReceivers = [];

    passEnds.forEach((passEnd) => {
      let playerAtEndOfPass = players.find(
        (x) => passEnd.e.join("|") === x.pos.join("|")
      );
      if (playerAtEndOfPass) {
        passReceivers.push({
          ...playerAtEndOfPass,
          team: "ball",
        });
      }
    });

    return [...players, ...arrowEnds, ...passReceivers];
  },

  getLinkedPlayer: (links, action, players, end) => {
    let relevantLink = links.find((x) => x.point === end);
    let relevantPlayer =
      relevantLink && (relevantLink.resolvedPlayer ?? relevantLink.player);

    let linkedPlayer =
      relevantLink && players.find((x) => x.id === relevantPlayer.id);

    if (!linkedPlayer) {
      return null;
    }

    let linkedPos = linkedPlayer.pos ?? linkedPlayer.e;

    let returnPlayer =
      action.subType === "handoff" ||
      (linkedPos[0] === action[end][0] && linkedPos[1] === action[end][1])
        ? linkedPlayer
        : null;

    return returnPlayer;
  },
  movePlayersToNextPosition: (
    players,
    object,
    allAffectedPlayers = [],
    court,
    links = []
  ) => {
    var changes = false;
    var error = false;
    let newPlayers = JSON.parse(JSON.stringify(players));
    let description = "";
    let playersWithBall = newPlayers.filter((x) => x.team === "ball");
    let playersOffense = newPlayers.filter(
      (x) => x.team === "offense" || x.team === "ball"
    );
    let hoops = newPlayers.filter((x) => x.subType === "hoop");

    let playersOffenseWithoutBall = newPlayers.filter(
      (x) => x.team === "offense"
    );

    let onlyPlayers = newPlayers.filter((x) => x.subType === "player");

    let affectedPlayers = [];
    allAffectedPlayers = object.sameTime ? allAffectedPlayers : [];

    switch (object.subType) {
      case "text": {
        description = 'Show Text "' + object.text + '"';
        break;
      }
      case "rectangle": {
        description = "Show Rectangle";
        break;
      }
      case "polygon": {
        description = "Show Polygon";
        break;
      }
      case "triangle": {
        description = "Show Triangle";
        break;
      }
      case "circle": {
        description = "Show Circle";
        break;
      }
      case "dribble": {
        let linkedPlayer = Helper.getLinkedPlayer(
          links,
          object,
          playersOffense,
          "s"
        );

        if (links.filter((x) => x.point === "s").length > 0 && !linkedPlayer) {
          description = "Unassigned Dribble";
          error = "Player linked to this action was already moved";
          break;
        }

        let closestPlayer =
          linkedPlayer ?? Helper.findClosestPlayer(object.s, playersOffense);

        if (closestPlayer) {
          description = "Dribble by Player " + closestPlayer.number;
          if (closestPlayer.team === "ball") {
            affectedPlayers.push(closestPlayer.id);
            if (allAffectedPlayers.includes(closestPlayer.id)) {
              error = "Player can't do two actions at the same time";
              break;
            }
            let currentArrowPos = Helper.onlyPoints(object);

            let arrowChangesS = Helper.transformArrow(
              currentArrowPos,
              { point: "s", pos: closestPlayer.pos },
              object.shape
            );
            let nextArrowPos = { ...currentArrowPos, ...arrowChangesS };

            if (!object.option) {
              closestPlayer.pos = object.e;
              changes = {
                type: "MOVE_PLAYER",
                movementArrow: {
                  ...nextArrowPos,
                  shape: object.shape,
                },
                id: closestPlayer.id,
              };
            }
          } else {
            error = "Player without ball is assigned a dribble";
          }
        } else {
          description = "Unassigned Dribble";
          error = "Action not assigned to a player";
        }

        break;
      }

      case "pass": {
        let linkedPlayer = Helper.getLinkedPlayer(
          links,
          object,
          playersOffense,
          "s"
        );

        if (links.filter((x) => x.point === "s").length > 0 && !linkedPlayer) {
          description = "Unassigned Pass";
          error = "Player linked to this action was already moved";
          break;
        }

        let closestPlayer =
          linkedPlayer ?? Helper.findClosestPlayer(object.s, playersOffense);

        if (closestPlayer) {
          if (closestPlayer.team === "ball") {
            affectedPlayers.push(closestPlayer.id);

            let destinationLinkedPlayer = Helper.getLinkedPlayer(
              links,
              object,
              playersOffense,
              "e"
            );
            if (
              links.filter((x) => x.point === "e").length > 0 &&
              !destinationLinkedPlayer
            ) {
              description = "Unassigned Pass";
              error = "Player linked to this action was already moved";
              break;
            }

            let closestPlayerToDestination =
              destinationLinkedPlayer ??
              Helper.findClosestPlayer(object.e, playersOffense);
            if (closestPlayerToDestination) {
              description =
                "Pass by Player " +
                closestPlayer.number +
                " to Player " +
                closestPlayerToDestination.number;
              affectedPlayers.push(closestPlayerToDestination.id);
              if (
                allAffectedPlayers.includes(closestPlayerToDestination.id) ||
                allAffectedPlayers.includes(closestPlayer.id)
              ) {
                error = "Player can't do two actions at the same time";
                break;
              }
              let currentArrowPos = Helper.onlyPoints(object);

              let arrowChangesS = Helper.transformArrow(
                currentArrowPos,
                { point: "s", pos: closestPlayer.pos },
                object.shape
              );
              let arrowAfterS = { ...currentArrowPos, ...arrowChangesS };

              let arrowChangesE = Helper.transformArrow(
                arrowAfterS,
                { point: "e", pos: closestPlayerToDestination.pos },
                object.shape
              );

              let nextArrowPos = { ...arrowAfterS, ...arrowChangesE };

              if (!object.option) {
                closestPlayer.team = "offense";
                closestPlayerToDestination.team = "ball";
                changes = {
                  type: "MOVE_BALL",
                  movementArrow: {
                    ...nextArrowPos,
                    shape: object.shape,
                  },
                  id: closestPlayer.id,
                  reveiverId: closestPlayerToDestination.id,
                };
              }
            } else {
              description = "Pass by Player " + closestPlayer.number;
              error = "No receiving player assigned to pass";
            }
          } else {
            description = "Pass by Player " + closestPlayer.number;
            error = "Player without ball is assigned a pass";
          }
        } else {
          description = "Unassigned Pass";
          error = "Action not assigned to a player";
        }
        break;
      }

      case "cut": {
        let linkedPlayer = Helper.getLinkedPlayer(
          links,
          object,
          onlyPlayers,
          "s"
        );

        if (links.filter((x) => x.point === "s").length > 0 && !linkedPlayer) {
          description = "Unassigned Cut";
          error = "Player linked to this action was already moved";
          break;
        }

        let closestPlayer =
          linkedPlayer ?? Helper.findClosestPlayer(object.s, onlyPlayers);

        if (closestPlayer) {
          affectedPlayers.push(closestPlayer.id);
          description =
            "Cut by Player " +
            closestPlayer.number +
            (["defense", "defense_alt"].includes(closestPlayer.team)
              ? " (Defense)"
              : " ");
          if (allAffectedPlayers.includes(closestPlayer.id)) {
            error = "Player can't do two actions at the same time";
            break;
          }

          if (
            closestPlayer.team === "offense" ||
            closestPlayer.team === "defense" ||
            closestPlayer.team === "defense_alt" ||
            (closestPlayer.team === "ball" &&
              Helper.checkIfOutOfBounds(closestPlayer.pos, court))
          ) {
            let currentArrowPos = Helper.onlyPoints(object);

            let arrowChangesS = Helper.transformArrow(
              currentArrowPos,
              { point: "s", pos: closestPlayer.pos },
              object.shape
            );
            let nextArrowPos = { ...currentArrowPos, ...arrowChangesS };

            if (!object.option) {
              closestPlayer.pos = object.e;
              if (closestPlayer.team === "defense_alt") {
                closestPlayer.angle = object.angle ?? 0;
              }
              changes = {
                type: "MOVE_PLAYER",
                movementArrow: {
                  ...nextArrowPos,
                  shape: object.shape,
                  windback: true,
                },
                id: closestPlayer.id,
              };
            }
          } else {
            error =
              "Player with the ball is assigned a cut (should be dribble)";
          }
        } else {
          description = "Unassigned Cut";
          error = "Action not assigned to a player";
        }
        break;
      }

      case "shot": {
        let linkedPlayer = Helper.getLinkedPlayer(
          links,
          object,
          onlyPlayers,
          "s"
        );

        if (links.filter((x) => x.point === "s").length > 0 && !linkedPlayer) {
          description = "Unassigned Shot";
          error = "Player linked to this action was already moved";
          break;
        }

        let closestPlayer =
          linkedPlayer ?? Helper.findClosestPlayer(object.s, playersOffense);

        if (closestPlayer) {
          if (closestPlayer.team === "ball") {
            description = "Shot by Player " + closestPlayer.number;
            affectedPlayers.push(closestPlayer.id);
            if (allAffectedPlayers.includes(closestPlayer.id)) {
              error = "Player can't do two actions at the same time";
              break;
            }

            let closestHoop = Helper.findClosestPlayer(object.e, hoops);

            if (closestHoop) {
              let currentArrowPos = Helper.onlyPoints(object);

              let arrowChangesS = Helper.transformArrow(
                currentArrowPos,
                { point: "s", pos: closestPlayer.pos },
                object.shape
              );
              let arrowAfterS = { ...currentArrowPos, ...arrowChangesS };

              let arrowChangesE = Helper.transformArrow(
                arrowAfterS,
                { point: "e", pos: closestHoop.pos },
                object.shape
              );

              let nextArrowPos = { ...arrowAfterS, ...arrowChangesE };

              if (!object.option) {
                closestPlayer.team = "offense";
                changes = {
                  type: "MOVE_BALL",
                  movementArrow: {
                    ...nextArrowPos,
                    shape: object.shape,
                  },
                  id: closestPlayer.id,
                  reveiverId: closestHoop.id,
                };
              }
            } else {
              description = "Shot by Player " + closestPlayer.number;
              error = "No hoop at the end of shot arrow";
            }
          } else {
            description = "Shot by Player " + closestPlayer.number;
            error = "Player without ball is assigned a shot";
          }
        } else {
          description = "Unassigned Shot";
          error = "Action not assigned to a player";
        }
        break;
      }

      case "screen": {
        let linkedPlayer = Helper.getLinkedPlayer(
          links,
          object,
          onlyPlayers,
          "s"
        );

        if (links.filter((x) => x.point === "s").length > 0 && !linkedPlayer) {
          description = "Unassigned Screen";
          error = "Player linked to this action was already moved";
          break;
        }

        let closestPlayer =
          linkedPlayer ?? Helper.findClosestPlayer(object.s, onlyPlayers);

        if (closestPlayer) {
          affectedPlayers.push(closestPlayer.id);

          description = "Screen by Player " + closestPlayer.number;
          if (allAffectedPlayers.includes(closestPlayer.id)) {
            error = "Player can't do two actions at the same time";
            break;
          }
          if (
            closestPlayer.team === "offense" ||
            closestPlayer.team === "defense" ||
            closestPlayer.team === "defense_alt"
          ) {
            let currentArrowPos = Helper.onlyPoints(object);

            let arrowChangesS = Helper.transformArrow(
              currentArrowPos,
              { point: "s", pos: closestPlayer.pos },
              object.shape
            );
            let nextArrowPos = { ...currentArrowPos, ...arrowChangesS };
            if (!object.option) {
              closestPlayer.pos = object.e;
              if (closestPlayer.team === "defense_alt") {
                closestPlayer.angle = object.angle ?? 0;
              }
              changes = {
                type: "MOVE_PLAYER",
                movementArrow: {
                  ...nextArrowPos,
                  shape: object.shape,
                },
                id: closestPlayer.id,
              };
            }
          } else {
            error =
              "Player with the ball is assigned a screen (should be dribble)";
          }
        } else {
          description = "Unassigned Screen";
          error = "Action not assigned to a player";
        }
        break;
      }

      case "handoff": {
        let linkedPlayerWithBall = Helper.getLinkedPlayer(
          links,
          object,
          playersWithBall,
          "s"
        );

        let closestPlayerWithBall =
          linkedPlayerWithBall ??
          Helper.findClosestPlayer(object.pos, playersWithBall);

        if (closestPlayerWithBall) {
          let linkedPlayerNoBall = Helper.getLinkedPlayer(
            links,
            object,
            playersOffenseWithoutBall,
            "e"
          );

          let closestPlayerToDestination =
            linkedPlayerNoBall ??
            Helper.findClosestPlayer(object.pos, playersOffenseWithoutBall);
          if (closestPlayerToDestination) {
            affectedPlayers.push(closestPlayerWithBall.id);
            affectedPlayers.push(closestPlayerToDestination.id);
            description =
              "Handoff by Player " +
              closestPlayerWithBall.number +
              " to Player #" +
              closestPlayerToDestination.number;
            if (
              allAffectedPlayers.includes(closestPlayerWithBall.id) ||
              allAffectedPlayers.includes(closestPlayerToDestination.id)
            ) {
              error = "Player can't do two actions at the same time";
              break;
            }
            if (!object.option) {
              closestPlayerWithBall.team = "offense";
              closestPlayerToDestination.team = "ball";
              changes = {
                type: "MOVE_BALL",
                movementArrow: {
                  s: closestPlayerWithBall.pos,
                  m: [
                    (closestPlayerWithBall.pos[0] +
                      closestPlayerToDestination.pos[0]) /
                      2,
                    (closestPlayerWithBall.pos[1] +
                      closestPlayerToDestination.pos[1]) /
                      2,
                  ],
                  e: closestPlayerToDestination.pos,
                  shape: 0,
                },
                id: closestPlayerWithBall.id,
                reveiverId: closestPlayerToDestination.id,
              };
            }
          } else {
            description = "Handoff by Player " + closestPlayerWithBall.number;
          }
        } else {
          description = "Unassigned Handoff";
          error = "Action not assigned to a player";
        }

        break;
      }
      default: {
        break;
      }
    }
    return {
      players: newPlayers,
      changes,
      error,
      description,
      action: object,
      allAffectedPlayers: object.sameTime
        ? [...allAffectedPlayers, ...affectedPlayers]
        : affectedPlayers,
    };
  },

  resolveLinkedPlayer: (link, links, depth = 0) => {
    if (depth > 10) {
      return false;
    }
    if (link.player.subType === "player" || link.player.subType === "hoop") {
      return link.player;
    } else {
      let newLink = links.find(
        (x) => x.object.id === link.player.id && x.point === "s"
      );
      if (!newLink) return false;
      return Helper.resolveLinkedPlayer(newLink, links, depth + 1);
    }
  },

  calculateNextPhase: (phase, court = { full: false }) => {
    let allAffectedPlayers = [];
    let currentOffset = {};
    let steps = [];
    let links = phase.links;

    let arrows = phase.objects
      .filter(
        (x) =>
          x.type === "arrow" ||
          x.subType === "handoff" ||
          x.subType === "text" ||
          x.subType === "rectangle" ||
          x.subType === "polygon" ||
          x.subType === "triangle" ||
          x.subType === "circle"
      )
      .map((x) => ({ ...x }));

    let players = phase.objects.filter(
      (x) => x.subType === "player" || x.subType === "hoop"
    );

    let objectsThatStay = phase.objects.filter((x) =>
      [
        "player",
        "hoop",
        "cone",
        "rectangle",
        "triangle",
        "circle",
        "polygon",
      ].includes(x.subType)
    );

    for (let arrow of arrows) {
      let relevantLinks = links.filter((x) => x.object.id === arrow.id);
      relevantLinks = relevantLinks.map((x) => ({
        ...x,
        resolvedPlayer: Helper.resolveLinkedPlayer(x, links),
      }));

      let data = Helper.movePlayersToNextPosition(
        players,
        arrow,
        allAffectedPlayers,
        court,
        relevantLinks
      );

      allAffectedPlayers = data.allAffectedPlayers;
      players = data.players;

      if (data.changes.type === "MOVE_PLAYER" && !data.action.option) {
        let offset =
          data.changes.type === "MOVE_BALL"
            ? [0, 0]
            : currentOffset[data.changes.id] ?? [0, 0];

        let nextArrowPos = data.changes.movementArrow;
        currentOffset[data.changes.id] = [
          offset[0] + (nextArrowPos.e[0] - nextArrowPos.s[0]),
          offset[1] + (nextArrowPos.e[1] - nextArrowPos.s[1]),
        ];
        data = {
          ...data,
          changes: {
            ...data.changes,
            offset: Helper.transposeCoord(offset, court),
          },
        };
      }

      steps.push({ ...data, phaseId: phase.id });
    }

    let updatedObjectsThatStay = objectsThatStay.map((object) => {
      let foundPlayer = players.find((x) => x.id === object.id);
      return foundPlayer ? foundPlayer : object;
    });

    return {
      steps: steps,
      nextState: { ...phase, objects: updatedObjectsThatStay },
    };
  },
  getHeight: (width, court) => {
    if (court.full) {
      return (
        (width / (court.length + 2 * court.sidelines)) *
        (court.width + 2 * court.sidelines)
      );
    } else {
      return (
        ((width / (court.width + 2 * court.sidelines)) *
          (court.length + 2 * court.sidelines)) /
        2
      );
    }
  },
  courtToSVG: (coords, svgWidth, court) => {
    let xValue, yValue;
    if (!court.full) {
      let courtWidth = court.width + 2 * court.sidelines;
      let widthRatio = svgWidth / courtWidth;
      xValue = (coords[0] + courtWidth / 2) * widthRatio;
      yValue = (coords[1] + court.sidelines) * widthRatio;
    } else {
      let courtLength = court.length + 2 * court.sidelines;
      let lengthRatio = svgWidth / courtLength;
      let courtWidth = court.width + 2 * court.sidelines;
      yValue = (courtWidth - (coords[0] + courtWidth / 2)) * lengthRatio;
      xValue = (coords[1] + court.sidelines) * lengthRatio;
    }

    return [xValue, yValue];
  },
  SVGToCourt: (coords, svgWidth, court) => {
    let xValue, yValue;
    if (!court.full) {
      let courtWidth = court.width + 2 * court.sidelines;
      let widthRatio = svgWidth / courtWidth;
      xValue = coords[0] / widthRatio - courtWidth / 2;
      yValue = coords[1] / widthRatio - court.sidelines;
    } else {
      let courtLength = court.length + 2 * court.sidelines;
      let lengthRatio = svgWidth / courtLength;
      let courtWidth = court.width + 2 * court.sidelines;
      xValue = courtWidth / 2 - coords[1] / lengthRatio;
      yValue = coords[0] / lengthRatio - court.sidelines;
    }
    return [xValue, yValue];
  },
  orthormalVector: (vector) => {
    if (vector[0] === 0 && vector[1] === 0) {
      return [0, 0];
    }
    return [
      (-1 * vector[1]) / Helper.lengthOfVector(vector),
      vector[0] / Helper.lengthOfVector(vector),
    ];
  },
  lengthOfVector: (point) => {
    return Math.sqrt(Math.pow(point[0], 2) + Math.pow(point[1], 2));
  },
  distance: (a, b) => {
    return Helper.lengthOfVector([a[0] - b[0], a[1] - b[1]]);
  },
  dampenSinusEnd: (distance) => {
    if (distance > 40) {
      return 1;
    } else if (distance <= 40 && distance >= 10) {
      return 1 * ((distance - 10) / 30);
    } else {
      return 0;
    }
  },
  scalarProduct: (x1, x2, y1, y2) => {
    return x1 * y1 + x2 * y2;
  },
  crossProduct: (x1, x2, y1, y2) => {
    return x1 * y2 - x2 * y1;
  },
  rotatePoint: (point, alpha) => {
    return [
      point[0] * Math.cos(alpha) - point[1] * Math.sin(alpha),
      point[0] * Math.sin(alpha) + point[1] * Math.cos(alpha),
    ];
  },

  rotatePointAroundCenter: (point, angle, center = { x: 0, y: 0 }) => {
    const angleInRadians = (angle * Math.PI) / 180;
    const cos = Math.cos(angleInRadians);
    const sin = Math.sin(angleInRadians);
    const x = point.x - center.x;
    const y = point.y - center.y;
    return {
      x: x * cos - y * sin + center.x,
      y: x * sin + y * cos + center.y,
    };
  },

  rotatePointAroundCenterArray: (point, angle, center) => {
    const angleInRadians = (angle * Math.PI) / 180;
    const cos = Math.cos(angleInRadians);
    const sin = Math.sin(angleInRadians);
    const x = point[0] - center[0];
    const y = point[1] - center[1];
    return [x * cos - y * sin + center[0], x * sin + y * cos + center[1]];
  },

  checkForPlayersToSnapTo: (point, players, radius) => {
    if (point === undefined) {
      return point;
    }

    if (players.length === 0) {
      return { pos: point, link: false };
    }

    let distances = players.map((player) => ({
      ...player,
      distance: Helper.distance(point, player.pos),
    }));

    distances.sort((a, b) => a.distance - b.distance);

    if (distances[0].distance < radius) {
      return { pos: distances[0].pos, link: true, player: distances[0] };
    } else {
      return { pos: point, link: false };
    }
  },
  transformArrow: (oldArrow, change, shape) => {
    if (change.point === "s") {
      switch (shape) {
        case 0: {
          return {
            s: change.pos,
            m: [
              change.pos[0] / 2 + oldArrow.e[0] / 2,
              change.pos[1] / 2 + oldArrow.e[1] / 2,
            ],
          };
        }
        case 1: {
          let angle = Helper.getAngleOfRotation(
            oldArrow.e,
            change.pos,
            oldArrow.s
          );

          let ratio = Helper.lengthRelation(oldArrow.e, change.pos, oldArrow.s);

          if (isNaN(angle) || isNaN(ratio)) {
            return oldArrow;
          }

          let newMiddle = Helper.transformPoint(
            oldArrow.e,
            oldArrow.m,
            -1 * angle,
            ratio
          );
          return { s: change.pos, m: newMiddle };
        }

        case 2: {
          return { s: change.pos };
        }
        case 3: {
          let angle = Helper.getAngleOfRotation(
            oldArrow.m,
            change.pos,
            oldArrow.s
          );
          let ratio = Helper.lengthRelation(oldArrow.m, change.pos, oldArrow.s);
          let newH1 = Helper.transformPoint(
            oldArrow.m,
            oldArrow.h1,
            -1 * angle,
            ratio
          );
          return { s: change.pos, h1: newH1 };
        }
        default: {
          return oldArrow;
        }
      }
    } else if (change.point === "e") {
      switch (shape) {
        case 0: {
          return {
            e: change.pos,
            m: [
              change.pos[0] / 2 + oldArrow.s[0] / 2,
              change.pos[1] / 2 + oldArrow.e[1] / 2,
            ],
          };
        }
        case 1: {
          let angle = Helper.getAngleOfRotation(
            oldArrow.s,
            change.pos,
            oldArrow.e
          );
          let ratio = Helper.lengthRelation(oldArrow.s, change.pos, oldArrow.e);
          let newMiddle = Helper.transformPoint(
            oldArrow.s,
            oldArrow.m,
            -1 * angle,
            ratio
          );
          return { e: change.pos, m: newMiddle };
        }

        case 2: {
          return { e: change.pos };
        }
        case 3: {
          let angle = Helper.getAngleOfRotation(
            oldArrow.m,
            change.pos,
            oldArrow.e
          );
          let ratio = Helper.lengthRelation(oldArrow.m, change.pos, oldArrow.e);
          let newH2 = Helper.transformPoint(
            oldArrow.m,
            oldArrow.h2,
            -1 * angle,
            ratio
          );
          return { e: change.pos, h2: newH2 };
        }
        default: {
          return oldArrow;
        }
      }
    } else if (change.point === "m") {
      if (shape === 3) {
        let h1Angle = Helper.getAngleOfRotation(
          oldArrow.s,
          change.pos,
          oldArrow.m
        );
        let h1Ratio = Helper.lengthRelation(oldArrow.s, change.pos, oldArrow.m);
        let newH1 = Helper.transformPoint(
          oldArrow.s,
          oldArrow.h1,
          -1 * h1Angle,
          h1Ratio
        );

        let h2Angle = Helper.getAngleOfRotation(
          oldArrow.e,
          change.pos,
          oldArrow.m
        );
        let h2Ratio = Helper.lengthRelation(oldArrow.e, change.pos, oldArrow.m);
        let newH2 = Helper.transformPoint(
          oldArrow.e,
          oldArrow.h2,
          -1 * h2Angle,
          h2Ratio
        );
        return {
          m: change.pos,
          h1: newH1,
          h2: newH2,
        };
      } else {
        return { m: change.pos };
      }
    } else if (change.point === "h1") {
      return { h1: change.pos };
    } else if (change.point === "h2") {
      return { h2: change.pos };
    }
  },

  snapActionToPlayer: (action, players, radius) => {
    let { shape, ends } = action;
    players = [...players];

    players.sort((a, b) => {
      if (a.subType === "player" && b.subType !== "player") {
        return -1;
      } else if (a.subType !== "player" && b.subType === "player") {
        return 1;
      } else {
        return 0;
      }
    });

    let links = [];

    let returnArrowPoints = {
      s: action.s,
      e: action.e,
      m: action.m,
      h1: action.h1,
      h2: action.h2,
    };

    var checkResultE = Helper.checkForPlayersToSnapTo(
      returnArrowPoints.e,
      players,
      radius
    );

    var checkResultS = Helper.checkForPlayersToSnapTo(
      returnArrowPoints.s,
      players,
      radius
    );

    if (checkResultE?.player?.id === checkResultS?.player?.id) {
      return {
        links: links,
        points: returnArrowPoints,
      };
    }

    if (
      checkResultE.link &&
      Helper.checkIfLinkable(checkResultE.player, action, "e")
    ) {
      let arrowChanges = Helper.transformArrow(
        returnArrowPoints,
        { point: "e", pos: checkResultE.pos },
        shape
      );
      returnArrowPoints = { ...returnArrowPoints, ...arrowChanges };
      if (ends.includes("e")) {
        links.push({
          player: checkResultE.player,
          point: "e",
        });
      }
    }

    if (
      checkResultS.link &&
      Helper.checkIfLinkable(action, checkResultS.player, "s")
    ) {
      let arrowChanges = Helper.transformArrow(
        returnArrowPoints,
        { point: "s", pos: checkResultS.pos },
        shape
      );
      returnArrowPoints = { ...returnArrowPoints, ...arrowChanges };
      if (ends.includes("s")) {
        links.push({
          player: checkResultS.player,
          point: "s",
        });
      }
    }
    return {
      links: links,
      points: returnArrowPoints,
    };
  },

  getAngleOfRotation: (c, x, y) => {
    let startVectorX = x[0] - c[0];
    let startVectorY = x[1] - c[1];
    let endVectorX = y[0] - c[0];
    let endVectorY = y[1] - c[1];
    return (
      Math.atan2(startVectorX, startVectorY) -
      Math.atan2(endVectorX, endVectorY)
    );
  },

  lengthRelation: (c, x, y) => {
    let startVectorX = x[0] - c[0];
    let startVectorY = x[1] - c[1];
    let endVectorX = y[0] - c[0];
    let endVectorY = y[1] - c[1];
    return (
      1 /
      (Helper.lengthOfVector([endVectorX, endVectorY]) /
        Helper.lengthOfVector([startVectorX, startVectorY]))
    );
  },

  transformPoint: (origin, start, angle, ratio) => {
    let rotatePart = Helper.rotatePoint(
      [start[0] - origin[0], start[1] - origin[1]],
      angle
    );
    let rotatePartX = rotatePart[0];
    let rotatePartY = rotatePart[1];
    let newX = origin[0] + rotatePartX * ratio;
    let newY = origin[1] + rotatePartY * ratio;
    return [newX, newY];
  },

  calculateLengthOfCurve: (shape, s, m, e, h1, h2) => {
    switch (shape) {
      case 0: {
        return Helper.distance(s, e);
      }
      case 1: {
        return Helper.distance(s, m) + Helper.distance(m, e);
      }
      case 2: {
        return Helper.distance(s, m) + Helper.distance(m, e);
      }
      default: {
        return (
          Helper.distance(s, h1) +
          Helper.distance(h1, m) +
          Helper.distance(m, h2) +
          Helper.distance(h2, e)
        );
      }
    }
  },
  calculatePointsOnCurve: (link, shape, s, m, e, h1, h2, radius) => {
    let lengthOfCurve = Helper.calculateLengthOfCurve(shape, s, m, e, h1, h2);
    let points = [];
    let step = 1 / (lengthOfCurve * 20);

    let startLink = link.includes("s");
    let endLink = link.includes("e");

    switch (shape) {
      case 0: {
        let modVector = [
          Helper.normalizeVector([e[0] - s[0], e[1] - s[1]])[0] * radius,
          Helper.normalizeVector([e[0] - s[0], e[1] - s[1]])[1] * radius,
        ];
        let startPoint = startLink
          ? [s[0] + modVector[0], s[1] + modVector[1]]
          : s;
        let endPoint = endLink ? [e[0] - modVector[0], e[1] - modVector[1]] : e;
        for (let t = 0; t <= 1; t = t + step) {
          let point = [
            startPoint[0] + t * (endPoint[0] - startPoint[0]),
            startPoint[1] + t * (endPoint[1] - startPoint[1]),
          ];
          points.push(point);
        }
        break;
      }
      case 1: {
        let cubicBezierPoints = Helper.convertQuadraticToCubic(s, m, e);
        if (startLink) {
          var { t: startT } = Helper.bezierIntersectionWithCircle(
            s,
            m,
            e,
            radius
          );
          cubicBezierPoints = Helper.splitBezierCurve(
            ...cubicBezierPoints,
            startT,
            1
          );
        }
        if (endLink) {
          var { t: endT } = Helper.bezierIntersectionWithCircle(
            e,
            m,
            s,
            radius
          );
          cubicBezierPoints = Helper.splitBezierCurve(
            ...cubicBezierPoints,
            1 - endT,
            0
          );
        }

        for (let t = 0; t <= 1; t = t + step) {
          let point = Helper.calcCubicBezierPointByT(...cubicBezierPoints, t);
          points.push(point);
        }

        break;
      }
      case 2: {
        let modVector = [
          Helper.normalizeVector([m[0] - s[0], m[1] - s[1]])[0] * radius,
          Helper.normalizeVector([m[0] - s[0], m[1] - s[1]])[1] * radius,
        ];
        let endModVector = [
          Helper.normalizeVector([e[0] - m[0], e[1] - m[1]])[0] * radius,
          Helper.normalizeVector([e[0] - m[0], e[1] - m[1]])[1] * radius,
        ];

        let startPoint = startLink
          ? [s[0] + modVector[0], s[1] + modVector[1]]
          : s;

        let endPoint = endLink
          ? [e[0] - endModVector[0], e[1] - endModVector[1]]
          : e;

        for (let t = 0; t <= 1; t = t + step) {
          let point = [
            startPoint[0] + t * (m[0] - startPoint[0]),
            startPoint[1] + t * (m[1] - startPoint[1]),
          ];
          points.push(point);
        }
        for (let t = 0.001; t <= 1; t = t + step) {
          let point = [
            m[0] + t * (endPoint[0] - m[0]),
            m[1] + t * (endPoint[1] - m[1]),
          ];
          points.push(point);
        }
        break;
      }
      case 3: {
        let bezierPoint1 = Helper.calculateBezierPoint(s, h1, m);
        let bezierPoint2 = Helper.calculateBezierPoint(m, h2, e);

        if (startLink) {
          let cubicBezierPoints = Helper.convertQuadraticToCubic(s, h1, m);
          var { t } = Helper.bezierIntersectionWithCircle(s, h1, m, radius);
          cubicBezierPoints = Helper.splitBezierCurve(
            ...cubicBezierPoints,
            t,
            1
          );
          for (let t = 0; t <= 1; t = t + step) {
            let point = Helper.calcCubicBezierPointByT(...cubicBezierPoints, t);
            points.push(point);
          }
        } else {
          for (let t = 0; t <= 1; t = t + step) {
            let point = [
              Math.pow(1 - t, 2) * s[0] +
                2 * (1 - t) * t * bezierPoint1[0] +
                Math.pow(t, 2) * m[0],
              Math.pow(1 - t, 2) * s[1] +
                2 * (1 - t) * t * bezierPoint1[1] +
                Math.pow(t, 2) * m[1],
            ];
            points.push(point);
          }
        }

        if (endLink) {
          let cubicBezierPoints = Helper.convertQuadraticToCubic(m, h2, e);
          var { t: endT3 } = Helper.bezierIntersectionWithCircle(
            e,
            h2,
            m,
            radius
          );
          cubicBezierPoints = Helper.splitBezierCurve(
            ...cubicBezierPoints,
            1 - endT3,
            0
          );
          for (let t = 0; t <= 1; t = t + step) {
            let point = Helper.calcCubicBezierPointByT(...cubicBezierPoints, t);
            points.push(point);
          }
        } else {
          for (let t = 0; t <= 1; t = t + step) {
            let point = [
              Math.pow(1 - t, 2) * m[0] +
                2 * (1 - t) * t * bezierPoint2[0] +
                Math.pow(t, 2) * e[0],
              Math.pow(1 - t, 2) * m[1] +
                2 * (1 - t) * t * bezierPoint2[1] +
                Math.pow(t, 2) * e[1],
            ];
            points.push(point);
          }
        }

        break;
      }
      default: {
      }
    }

    return points;
  },

  calculateBezierPoint: (s, m, e) => {
    let bezierPointX = (s[0] + e[0]) / 2 + (m[0] - (s[0] + e[0]) / 2) * 2;
    let bezierPointY = (s[1] + e[1]) / 2 + (m[1] - (s[1] + e[1]) / 2) * 2;
    return [bezierPointX, bezierPointY];
  },

  adjustPointsV2: (action, links, radius) => {
    var startLink = links.filter((link) => link.point === "s")[0] ?? false;
    var endLink = links.filter((link) => link.point === "e")[0] ?? false;
    let startDistance = startLink
      ? distanceAtEnds[action.subType].start * radius
      : 0;
    let endDistance = endLink ? distanceAtEnds[action.subType].end * radius : 0;
    let { e, m, s, h1, h2, shape } = action;

    if (!startLink && !endLink) {
      return { s, e };
    }

    switch (shape) {
      case 0: {
        let startModVector = [
          Helper.normalizeVector([e[0] - s[0], e[1] - s[1]])[0] * startDistance,
          Helper.normalizeVector([e[0] - s[0], e[1] - s[1]])[1] * startDistance,
        ];
        let endModVector = [
          Helper.normalizeVector([e[0] - s[0], e[1] - s[1]])[0] * endDistance,
          Helper.normalizeVector([e[0] - s[0], e[1] - s[1]])[1] * endDistance,
        ];
        let startPoint = startLink
          ? [s[0] + startModVector[0], s[1] + startModVector[1]]
          : s;
        let endPoint =
          endLink && endLink.player.subType !== "hoop"
            ? [e[0] - endModVector[0], e[1] - endModVector[1]]
            : e;
        return {
          s: startPoint,
          e: endPoint,
        };
      }
      case 1: {
        let startPoint = startLink
          ? Helper.bezierIntersectionWithCircle(s, m, e, startDistance).pos
          : s;
        let endPoint = endLink
          ? Helper.bezierIntersectionWithCircle(e, m, s, endDistance).pos
          : e;

        return {
          s: startPoint,
          e: endPoint,
        };
      }
      case 2: {
        let modVectorStart = [
          Helper.normalizeVector([m[0] - s[0], m[1] - s[1]])[0] * startDistance,
          Helper.normalizeVector([m[0] - s[0], m[1] - s[1]])[1] * startDistance,
        ];
        let modVectorEnd = [
          Helper.normalizeVector([e[0] - m[0], e[1] - m[1]])[0] * endDistance,
          Helper.normalizeVector([e[0] - m[0], e[1] - m[1]])[1] * endDistance,
        ];

        let startPoint = startLink
          ? [s[0] + modVectorStart[0], s[1] + modVectorStart[1]]
          : s;

        if (isNaN(startPoint[0]) || isNaN(startPoint[1])) {
          startPoint = s;
        }

        let endPoint = endLink
          ? [e[0] - modVectorEnd[0], e[1] - modVectorEnd[1]]
          : e;

        if (isNaN(endPoint[0]) || isNaN(endPoint[1])) {
          endPoint = e;
        }

        return {
          s: startPoint,
          e: endPoint,
        };
      }

      case 3: {
        let bezierPointEnd = Helper.calculateBezierPoint(m, h2, e);
        let bezierPointStart = Helper.calculateBezierPoint(s, h1, m);

        let modVectorStart = [
          Helper.normalizeVector([
            bezierPointStart[0] - s[0],
            bezierPointStart[1] - s[1],
          ])[0] * startDistance,
          Helper.normalizeVector([
            bezierPointStart[0] - s[0],
            bezierPointStart[1] - s[1],
          ])[1] * startDistance,
        ];
        let modVectorEnd = [
          Helper.normalizeVector([
            e[0] - bezierPointEnd[0],
            e[1] - bezierPointEnd[1],
          ])[0] * endDistance,
          Helper.normalizeVector([
            e[0] - bezierPointEnd[0],
            e[1] - bezierPointEnd[1],
          ])[1] * endDistance,
        ];

        let startPoint = startLink
          ? [s[0] + modVectorStart[0], s[1] + modVectorStart[1]]
          : s;
        let endPoint =
          endLink && endLink.player.subType !== "hoop"
            ? [e[0] - modVectorEnd[0], e[1] - modVectorEnd[1]]
            : e;
        return {
          s: startPoint,
          e: endPoint,
        };
      }
      default: {
        return { s, e };
      }
    }
  },

  adjustPoints: (shape, links, s, m, e, h1, h2, radius, subType = "cut") => {
    var startLink = links.filter((link) => link.point === "s")[0] ?? false;
    var endLink = links.filter((link) => link.point === "e")[0] ?? false;
    let endRadius = subType === "screen" ? radius * 2 : radius;

    if (!startLink && !endLink) {
      return { s, e };
    }

    switch (shape) {
      case 0: {
        let startModVector = [
          Helper.normalizeVector([e[0] - s[0], e[1] - s[1]])[0] * radius,
          Helper.normalizeVector([e[0] - s[0], e[1] - s[1]])[1] * radius,
        ];
        let endModVector = [
          Helper.normalizeVector([e[0] - s[0], e[1] - s[1]])[0] * endRadius,
          Helper.normalizeVector([e[0] - s[0], e[1] - s[1]])[1] * endRadius,
        ];
        let startPoint = startLink
          ? [s[0] + startModVector[0], s[1] + startModVector[1]]
          : s;
        let endPoint =
          endLink && endLink.player.subType !== "hoop"
            ? [e[0] - endModVector[0], e[1] - endModVector[1]]
            : e;
        return {
          s: startPoint,
          e: endPoint,
        };
      }
      case 1: {
        let startPoint = startLink
          ? Helper.bezierIntersectionWithCircle(s, m, e, radius).pos
          : s;
        let endPoint =
          endLink && endLink.player.subType !== "hoop"
            ? Helper.bezierIntersectionWithCircle(e, m, s, endRadius).pos
            : e;
        return {
          s: startPoint,
          e: endPoint,
        };
      }
      case 2: {
        let modVectorStart = [
          Helper.normalizeVector([m[0] - s[0], m[1] - s[1]])[0] * radius,
          Helper.normalizeVector([m[0] - s[0], m[1] - s[1]])[1] * radius,
        ];
        let modVectorEnd = [
          Helper.normalizeVector([e[0] - m[0], e[1] - m[1]])[0] * endRadius,
          Helper.normalizeVector([e[0] - m[0], e[1] - m[1]])[1] * endRadius,
        ];

        let startPoint = startLink
          ? [s[0] + modVectorStart[0], s[1] + modVectorStart[1]]
          : s;
        let endPoint =
          endLink && endLink.player.subType !== "hoop"
            ? [e[0] - modVectorEnd[0], e[1] - modVectorEnd[1]]
            : e;
        return {
          s: startPoint,
          e: endPoint,
        };
      }

      case 3: {
        let bezierPointEnd = Helper.calculateBezierPoint(m, h2, e);
        let bezierPointStart = Helper.calculateBezierPoint(s, h1, m);

        let modVectorStart = [
          Helper.normalizeVector([
            bezierPointStart[0] - s[0],
            bezierPointStart[1] - s[1],
          ])[0] * radius,
          Helper.normalizeVector([
            bezierPointStart[0] - s[0],
            bezierPointStart[1] - s[1],
          ])[1] * radius,
        ];
        let modVectorEnd = [
          Helper.normalizeVector([
            e[0] - bezierPointEnd[0],
            e[1] - bezierPointEnd[1],
          ])[0] * endRadius,
          Helper.normalizeVector([
            e[0] - bezierPointEnd[0],
            e[1] - bezierPointEnd[1],
          ])[1] * endRadius,
        ];

        let startPoint = startLink
          ? [s[0] + modVectorStart[0], s[1] + modVectorStart[1]]
          : s;
        let endPoint =
          endLink && endLink.player.subType !== "hoop"
            ? [e[0] - modVectorEnd[0], e[1] - modVectorEnd[1]]
            : e;
        return {
          s: startPoint,
          e: endPoint,
        };
      }
      default: {
        return { s, e };
      }
    }
  },
  calcCubicBezierPointByT: (s, bezierPoint1, bezierPoint2, e, t) => {
    return [
      Math.pow(1 - t, 3) * s[0] +
        3 * Math.pow(1 - t, 2) * t * bezierPoint1[0] +
        3 * (1 - t) * Math.pow(t, 2) * bezierPoint2[0] +
        Math.pow(t, 3) * e[0],
      Math.pow(1 - t, 3) * s[1] +
        3 * Math.pow(1 - t, 2) * t * bezierPoint1[1] +
        3 * (1 - t) * Math.pow(t, 2) * bezierPoint2[1] +
        Math.pow(t, 3) * e[1],
    ];
  },
  calcQuadraticBezierPointByT: (s, bezierPoint, e, t) => {
    return [
      Math.pow(1 - t, 2) * s[0] +
        2 * (1 - t) * t * bezierPoint[0] +
        Math.pow(t, 2) * e[0],
      Math.pow(1 - t, 2) * s[1] +
        2 * (1 - t) * t * bezierPoint[1] +
        Math.pow(t, 2) * e[1],
    ];
  },
  convertQuadraticToCubic: (s, m, e) => {
    let bezierPoint = Helper.calculateBezierPoint(s, m, e);
    return [
      s,
      [
        s[0] + (2 / 3) * (bezierPoint[0] - s[0]),
        s[1] + (2 / 3) * (bezierPoint[1] - s[1]),
      ],
      [
        e[0] + (2 / 3) * (bezierPoint[0] - e[0]),
        e[1] + (2 / 3) * (bezierPoint[1] - e[1]),
      ],
      e,
    ];
  },
  bezierIntersectionWithCircle: (s, m, e, r) => {
    //secant algo

    let bezierPoint = Helper.calculateBezierPoint(s, m, e);
    var t0 = 0;
    var t1 = 0.01;
    let i = 0;
    var d0, d1, nextT;
    d1 = 1000;
    while (i < 100 && d1 > 0.000001) {
      i++;
      d0 =
        r -
        Helper.distance(
          Helper.calcQuadraticBezierPointByT(s, bezierPoint, e, t0),
          s
        );
      d1 =
        r -
        Helper.distance(
          Helper.calcQuadraticBezierPointByT(s, bezierPoint, e, t1),
          s
        );
      nextT = t1 - ((t1 - t0) / (d1 - d0)) * d1;
      t0 = t1;
      t1 = nextT;
    }

    return {
      pos: isNaN(nextT)
        ? s
        : Helper.calcQuadraticBezierPointByT(s, bezierPoint, e, nextT),
      t: nextT,
    };
  },

  calcCubicBezierPointByT: (s, bezierPoint1, bezierPoint2, e, t) => {
    return [
      Math.pow(1 - t, 3) * s[0] +
        3 * Math.pow(1 - t, 2) * t * bezierPoint1[0] +
        3 * (1 - t) * Math.pow(t, 2) * bezierPoint2[0] +
        Math.pow(t, 3) * e[0],
      Math.pow(1 - t, 3) * s[1] +
        3 * Math.pow(1 - t, 2) * t * bezierPoint1[1] +
        3 * (1 - t) * Math.pow(t, 2) * bezierPoint2[1] +
        Math.pow(t, 3) * e[1],
    ];
  },

  cubicBezierIntersectionWithCircle: (s, m1, m2, e, r) => {
    //secant algo
    var t0 = 0;
    var t1 = 0.01;
    let i = 0;
    var d0, d1, nextT;
    d1 = 1000;
    while (i < 100 && d1 > 0.000001) {
      i++;
      d0 =
        r -
        Helper.distance(Helper.calcCubicBezierPointByT(s, m1, m2, e, t0), s);
      d1 =
        r -
        Helper.distance(Helper.calcCubicBezierPointByT(s, m1, m2, e, t1), s);
      nextT = t1 - ((t1 - t0) / (d1 - d0)) * d1;
      t0 = t1;
      t1 = nextT;
    }
    return {
      pos: Helper.calcCubicBezierPointByT(s, m1, m2, e, nextT),
      t: nextT,
    };
  },

  splitBezierCurve: (s, m1, m2, e, t, part) => {
    let i = 1 - t;
    let tempE = [i * s[0] + t * m1[0], i * s[1] + t * m1[1]];
    let tempF = [i * m1[0] + t * m2[0], i * m1[1] + t * m2[1]];
    let tempG = [i * m2[0] + t * e[0], i * m2[1] + t * e[1]];
    let tempH = [i * tempE[0] + t * tempF[0], i * tempE[1] + t * tempF[1]];
    let tempJ = [i * tempF[0] + t * tempG[0], i * tempF[1] + t * tempG[1]];
    let tempK = [i * tempH[0] + t * tempJ[0], i * tempH[1] + t * tempJ[1]];

    if (part === 1) {
      return [tempK, tempJ, tempG, e];
    } else {
      return [s, tempE, tempH, tempK];
    }
  },

  printCubicBezierPath: (s, m1, m2, e) => {
    let path = "";
    path = `${path} M${s[0].toFixed(2)} ${s[1].toFixed(2)} `;
    path = `${path} C${m1[0].toFixed(2)} ${m1[1].toFixed(2)} ${m2[0].toFixed(
      2
    )} ${m2[1].toFixed(2)} ${e[0].toFixed(2)} ${e[1].toFixed(2)} `;
    return path;
  },

  createHandoffPath: (s, m, e, radius) => {
    var cubicBezierPoints = Helper.convertQuadraticToCubic(s, m, e);

    let startPart = Helper.splitBezierCurve(
      cubicBezierPoints[0],
      cubicBezierPoints[1],
      cubicBezierPoints[2],
      cubicBezierPoints[3],
      0.5,
      0
    );

    var { t } = Helper.cubicBezierIntersectionWithCircle(
      ...startPart.reverse(),
      radius * 1.2
    );
    startPart = Helper.splitBezierCurve(...startPart, t, 1).reverse();

    var { t: endT } = Helper.cubicBezierIntersectionWithCircle(
      ...startPart,
      radius
    );
    startPart = Helper.splitBezierCurve(...startPart, endT, 1);

    let endPart = Helper.splitBezierCurve(
      cubicBezierPoints[0],
      cubicBezierPoints[1],
      cubicBezierPoints[2],
      cubicBezierPoints[3],
      0.5,
      1
    );

    var { t: t2 } = Helper.cubicBezierIntersectionWithCircle(
      ...endPart,
      radius * 1.2
    );
    endPart = Helper.splitBezierCurve(...endPart, t2, 1);

    var { t: t3 } = Helper.cubicBezierIntersectionWithCircle(
      ...endPart.reverse(),
      radius
    );
    endPart = Helper.splitBezierCurve(...endPart.reverse(), 1 - t3, 0);

    return [
      Helper.printCubicBezierPath(...startPart),
      Helper.printCubicBezierPath(...endPart),
    ];
  },

  createPathV2: (action, links, radius) => {
    try {
      let { shape, s, m, e, h1, h2 } = action;
      let adjustedEndPoints = Helper.adjustPointsV2(action, links, radius);

      var startLink = links.filter((link) => link.point === "s")[0] ?? false;
      var endLink = links.filter((link) => link.point === "e")[0] ?? false;
      let startDistance = startLink
        ? distanceAtEnds[action.subType].start * radius
        : 0;
      let endDistance = endLink
        ? distanceAtEnds[action.subType].end * radius
        : 0;

      switch (shape) {
        case 0: {
          return (
            " M" +
            adjustedEndPoints.s[0].toFixed(3) +
            " " +
            adjustedEndPoints.s[1].toFixed(3) +
            " L" +
            adjustedEndPoints.e[0].toFixed(3) +
            " " +
            adjustedEndPoints.e[1].toFixed(3)
          );
        }

        case 1: {
          var cubicBezierPoints = Helper.convertQuadraticToCubic(s, m, e);

          if (startLink) {
            var { t } = Helper.bezierIntersectionWithCircle(
              s,
              m,
              e,
              startDistance
            );

            if (isNaN(t)) {
              return "";
            }
            cubicBezierPoints = Helper.splitBezierCurve(
              cubicBezierPoints[0],
              cubicBezierPoints[1],
              cubicBezierPoints[2],
              cubicBezierPoints[3],
              t,
              1
            );
          }
          if (endLink) {
            var { t: t2 } = Helper.bezierIntersectionWithCircle(
              e,
              m,
              s,
              endDistance
            );

            if (isNaN(t2)) {
              return "";
            }
            cubicBezierPoints = Helper.splitBezierCurve(
              cubicBezierPoints[0],
              cubicBezierPoints[1],
              cubicBezierPoints[2],
              cubicBezierPoints[3],
              1 - t2,
              0
            );
          }

          return (
            " M" +
            cubicBezierPoints[0][0].toFixed(3) +
            " " +
            cubicBezierPoints[0][1].toFixed(3) +
            " C" +
            cubicBezierPoints[1][0].toFixed(3) +
            " " +
            cubicBezierPoints[1][1].toFixed(3) +
            " " +
            cubicBezierPoints[2][0].toFixed(3) +
            " " +
            cubicBezierPoints[2][1].toFixed(3) +
            " " +
            cubicBezierPoints[3][0].toFixed(3) +
            " " +
            cubicBezierPoints[3][1].toFixed(3)
          );
        }

        case 2: {
          return (
            " M" +
            adjustedEndPoints.s[0].toFixed(3) +
            " " +
            adjustedEndPoints.s[1].toFixed(3) +
            " L" +
            m[0].toFixed(3) +
            " " +
            m[1].toFixed(3) +
            " L" +
            adjustedEndPoints.e[0].toFixed(3) +
            " " +
            adjustedEndPoints.e[1].toFixed(3)
          );
        }

        case 3: {
          var cubicBezierPoints1 = Helper.convertQuadraticToCubic(s, h1, m);
          var cubicBezierPoints2 = Helper.convertQuadraticToCubic(m, h2, e);
          if (startLink) {
            let { t: t1 } = Helper.bezierIntersectionWithCircle(
              s,
              h1,
              m,
              startDistance
            );
            cubicBezierPoints1 = Helper.splitBezierCurve(
              cubicBezierPoints1[0],
              cubicBezierPoints1[1],
              cubicBezierPoints1[2],
              cubicBezierPoints1[3],
              t1,
              1
            );
          }
          if (endLink) {
            let { t: t2 } = Helper.bezierIntersectionWithCircle(
              e,
              h2,
              m,
              endDistance
            );
            cubicBezierPoints2 = Helper.splitBezierCurve(
              cubicBezierPoints2[0],
              cubicBezierPoints2[1],
              cubicBezierPoints2[2],
              cubicBezierPoints2[3],
              1 - t2,
              0
            );
          }

          return (
            " M" +
            cubicBezierPoints1[0][0].toFixed(3) +
            " " +
            cubicBezierPoints1[0][1].toFixed(3) +
            " C" +
            cubicBezierPoints1[1][0].toFixed(3) +
            " " +
            cubicBezierPoints1[1][1].toFixed(3) +
            " " +
            cubicBezierPoints1[2][0].toFixed(3) +
            " " +
            cubicBezierPoints1[2][1].toFixed(3) +
            " " +
            m[0].toFixed(3) +
            " " +
            m[1].toFixed(3) +
            " C" +
            cubicBezierPoints2[1][0].toFixed(3) +
            " " +
            cubicBezierPoints2[1][1].toFixed(3) +
            " " +
            cubicBezierPoints2[2][0].toFixed(3) +
            " " +
            cubicBezierPoints2[2][1].toFixed(3) +
            " " +
            cubicBezierPoints2[3][0].toFixed(3) +
            " " +
            cubicBezierPoints2[3][1].toFixed(3)
          );
        }
        default: {
          return "";
        }
      }
    } catch (e) {
      console.log(e);
      return "";
    }
  },
  createWavyPath: (points) => {
    let path = "M" + points[0][0] + " " + points[0][1] + " ";
    let sumOfPathLength = 0;
    let pixelPerFoot = 13.6;
    for (let i = 0; i < points.length - 2; i++) {
      let distFromEnd = Helper.lengthOfVector([
        points[i][0] - points[points.length - 1][0],
        points[i][1] - points[points.length - 1][1],
      ]);
      let startPoint = points[i];
      let endPoint = points[i + 1];
      let middlePoint = [
        (startPoint[0] + endPoint[0]) / 2,
        (startPoint[1] + endPoint[1]) / 2,
      ];
      let diffVector = [
        startPoint[0] - endPoint[0],
        startPoint[1] - endPoint[1],
      ];
      sumOfPathLength += Helper.lengthOfVector(diffVector);
      let orthormalVector = Helper.orthormalVector(diffVector);
      let sinusFrequence = sumOfPathLength / 0.3;
      let sinusAmplitude = 0.55;
      let factor =
        Math.sin(sinusFrequence) *
        sinusAmplitude *
        Helper.dampenSinusEnd(distFromEnd * pixelPerFoot);

      let sinusPoint = [
        middlePoint[0] + factor * orthormalVector[0],
        middlePoint[1] + factor * orthormalVector[1],
      ];

      //round sinusPoint coords to 1 digits
      sinusPoint = Helper.roundCoords(sinusPoint, 2);

      path =
        path +
        (i !== points.length - 3 || i % 2 === 1
          ? (i % 2 === 0 ? "Q " : "") +
            sinusPoint[0] +
            " " +
            sinusPoint[1] +
            " "
          : "");
    }
    let lastPoint = Helper.roundCoords(points[points.length - 1], 2);
    path = path + "L " + lastPoint[0] + " " + lastPoint[1];
    return path;
  },

  mergeLinks: (oldLinks, newLinks) => {
    let tempLinks = oldLinks;
    for (let link of newLinks) {
      let foundIndex = oldLinks.findIndex(
        (x) => x.object === link.object && x.point === link.point
      );
      if (foundIndex !== -1) {
        tempLinks.splice(foundIndex, 1);
      }
      tempLinks.push(link);
    }
    return tempLinks;
  },
  onlyPoints: (object) => {
    return {
      s: object.s,
      m: object.m,
      e: object.e,
      h1: object.h1,
      h2: object.h2,
    };
  },

  highlightHandoffPlayers: (point, players, uId, radius, strokeWidth) => {
    let { drawPaths, hoveredPlayers } = Helper.checkForHandoffPlayers(
      point,
      players,
      radius
    );

    let path0, path1;
    if (uId) {
      path0 = document.getElementById(uId + "_handoff_path_0");
      path1 = document.getElementById(uId + "_handoff_path_1");
    } else {
      //create new svg path
      path0 = document.getElementById("drag_handoff_path_0");
      if (!path0) {
        path0 = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path0.setAttribute("id", "drag_handoff_path_0");
        path0.setAttribute("class", "handoff_path");
        path0.setAttribute("stroke", "rgba(240, 241, 243,0.6  )");
        path0.setAttribute("stroke-width", strokeWidth);
        path0.setAttribute("fill", "none");
        document.getElementById("editor_court").appendChild(path0);
      }
      path1 = document.getElementById("drag_handoff_path_1");
      if (!path1) {
        path1 = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path1.setAttribute("id", "drag_handoff_path_1");
        path1.setAttribute("class", "handoff_path");
        path1.setAttribute("stroke", "rgba(240, 241, 243,0.6  )");
        path1.setAttribute("stroke-width", strokeWidth);
        path1.setAttribute("fill", "none");
        document.getElementById("editor_court").appendChild(path1);
      }
    }
    //add svg path to node with id editor
    if (drawPaths.length === 2) {
      if (path0) {
        path0.style.visibility = "visible";
        path0.setAttribute("d", drawPaths[0]);
      }
      if (path1) {
        path1.style.visibility = "visible";
        path1.setAttribute("d", drawPaths[1]);
      }
    } else {
      if (path0) {
        path0.style.visibility = "hidden";
      }
      if (path1) {
        path1.style.visibility = "hidden";
      }
    }

    players.forEach((player, i) => {
      if (hoveredPlayers.includes(player.id)) {
        document
          .getElementById("editor_" + player.id)
          ?.classList?.add("target_hover");
      } else {
        document
          .getElementById("editor_" + player.id)
          ?.classList?.remove("target_hover");
      }
    });
  },

  findHandOffLinks: (point, players, ends = ["s", "e"]) => {
    if (!players) return;

    let playersWithBall = players.filter((player) => player.team === "ball");
    let playersOffense = players.filter((player) => player.team === "offense");

    let closestPlayerWithBall = Helper.findClosestPlayer(
      point,
      playersWithBall,
      maxHandoffDistance
    );
    let closestPlayerOffense = Helper.findClosestPlayer(
      point,
      playersOffense.filter(
        (player) =>
          player.pos.join("|") !== closestPlayerWithBall?.pos?.join("|")
      ),
      maxHandoffDistance
    );
    if (
      closestPlayerWithBall &&
      closestPlayerOffense &&
      ends.includes("s") &&
      ends.includes("e")
    ) {
      return [
        {
          player: closestPlayerWithBall,
          point: "s",
        },
        {
          player: closestPlayerOffense,
          point: "e",
        },
      ];
    }
    return [];
  },

  getLinkedHandoffPaths(point, players, radius, links) {
    let handOffStartLink = links.find(
      (link) => link.point === "s" && link.object.subType === "handoff"
    );
    let handOffEndLink = links.find(
      (link) => link.point === "e" && link.object.subType === "handoff"
    );

    if (!handOffStartLink || !handOffEndLink) return [];

    let closestPlayerWithBall = players.find(
      (player) => player.id === handOffStartLink.player.id
    );
    let closestPlayerOffense = players.find(
      (player) => player.id === handOffEndLink.player.id
    );

    let drawPaths = Helper.createHandoffPath(
      closestPlayerWithBall.pos,
      point,
      closestPlayerOffense.pos,
      radius
    );

    return drawPaths;
  },

  checkForHandoffPlayers: (point, players, playerRadius = 0) => {
    if (!players) return;

    let hoveredPlayers = [];
    let playersWithBall = players.filter((player) => player.team === "ball");
    let playersOffense = players.filter((player) => player.team === "offense");

    let closestPlayerWithBall = Helper.findClosestPlayer(
      point,
      playersWithBall,
      maxHandoffDistance
    );

    let closestPlayerOffense = Helper.findClosestPlayer(
      point,
      playersOffense.filter(
        (player) =>
          player.pos.join("|") !== closestPlayerWithBall?.pos?.join("|")
      ),
      maxHandoffDistance
    );

    if (closestPlayerWithBall) {
      hoveredPlayers.push(closestPlayerWithBall.id);
    }
    if (closestPlayerOffense) {
      hoveredPlayers.push(closestPlayerOffense.id);
    }
    let drawPaths = [];
    if (closestPlayerWithBall && closestPlayerOffense) {
      drawPaths = Helper.createHandoffPath(
        closestPlayerWithBall.pos,
        point,
        closestPlayerOffense.pos,
        playerRadius
      );
    }
    return { drawPaths, hoveredPlayers };
  },

  checkIfLinkable: (object, action, end) => {
    if (end === "e") {
      if (object.subType === "hoop" && action.subType !== "shot") return false;
      if (object.subType !== "hoop" && action.subType === "shot") return false;
    } else if (end === "s") {
      if (object.subType === "hoop") return false;
    }

    return true;
  },

  highlightPossibleLinks: (action, players, radius) => {
    let hoveredPlayers = [];

    let points = [
      { pos: action.s, end: "s", distances: [] },
      { pos: action.e, end: "e", distances: [] },
    ];

    players
      .filter((x) => x.id !== action.id)
      .forEach((player) => {
        points.forEach((point) => {
          point.distances.push({
            player: player,
            point: point,
            distance: Helper.distance(player.pos, point.pos),
          });
        });
      });

    points.forEach((point) => {
      point.distances.sort((a, b) => a.distance - b.distance);
      if (
        point.distances[0].distance < radius &&
        Helper.checkIfLinkable(point.distances[0].player, action, point.end)
      ) {
        hoveredPlayers.push(point.distances[0].player.id);
      }
    });

    players.forEach((player, i) => {
      if (hoveredPlayers.includes(player.id)) {
        document
          .getElementById("editor_" + player.id)
          ?.classList?.add("target_hover");
      } else {
        document
          .getElementById("editor_" + player.id)
          ?.classList?.remove("target_hover");
      }
    });
  },

  checkForPlayersAtThisPoint: (
    points,
    players,
    radius,
    ignore = [],
    actionType = "cut"
  ) => {
    let hoveredPlayers = [];
    let distancePerPoint = points.map((point) => []);

    players
      .filter((x) => !ignore.includes(x.id))
      .forEach((player) => {
        points.forEach((point, index) => {
          distancePerPoint[index].push({
            player: player,
            distance: Helper.distance(player.pos, point),
          });
        });
      });
    distancePerPoint.forEach((distanceArray, index) => {
      distanceArray.sort((a, b) => a.distance - b.distance);
      distanceArray.forEach((item, index) => {
        if (index === 0 && item.distance < radius) {
          hoveredPlayers.push(item.player.id);
        }
      });
    });
    players.forEach((player, i) => {
      if (hoveredPlayers.includes(player.id)) {
        document
          .getElementById("editor_" + player.id)
          ?.classList?.add("target_hover");
      } else {
        document
          .getElementById("editor_" + player.id)
          ?.classList?.remove("target_hover");
      }
    });
  },
  correctForOutOfCourt: (point, court) => {
    let maxX = court.full
      ? court.length + court.sidelines
      : court.width / 2 + court.sidelines;
    return [Math.min(point[0], maxX - 0.5), point[1]];
  },
  safeNumber: (number) => {
    return number === 0 ? 1000 : Helper.roundNumber(number, 2);
  },
  convertPixelToViewBox: (input, court, phaseWidth) => {
    let editorWidthInFeet = court.full ? court.length : court.width;
    return (input / phaseWidth) * editorWidthInFeet;
  },
  convertViewBoxToPixel: (input, court, phaseWidth) => {
    let editorWidthInFeet =
      (court.full ? court.length : court.width) + 2 * court.sidelines;
    return (input * phaseWidth) / editorWidthInFeet;
  },

  objectMap: (object, mapFn) => {
    return Object.keys(object).reduce(function (result, key) {
      result[key] = mapFn(object[key]);
      return result;
    }, {});
  },

  objectSizes: {
    playerBoxWidth: [3.5, 4],
    playerCircleRadius: [3.5 * 0.9 * 0.5, 4 * 0.9 * 0.5],
    handOffWidth: [3.5 * 0.8, 5 * 0.8],
    coneWidth: [3.5 * 0.8, 5 * 0.8],
    arrowStrokeWidth: [0.22, 0.3],
    arrowWidth: [10, 10],
  },

  getPlayerRadiusFromState: (state) => {
    return (
      state.settings.objectSizeFactor *
      Helper.objectSizes.playerCircleRadius[
        state.settings.editor.court.full ? 1 : 0
      ]
    );
  },

  getPlayerRadiusFromSettings: (settings) => {
    return (
      settings.objectSizeFactor *
      Helper.objectSizes.playerCircleRadius[settings.editor.court.full ? 1 : 0]
    );
  },

  getObjectSizes: (phaseWidth, court, type = "viewbox") => {
    //object widths are standardized for 700px and no sidelines

    return {
      viewbox: Helper.objectMap(
        Helper.objectSizes,
        (size) => size[court.full ? 1 : 0]
      ),
      pixel: Helper.objectMap(
        Helper.objectSizes,
        (size) =>
          Helper.convertViewBoxToPixel(
            size[court.full ? 1 : 0],
            court,
            phaseWidth
          ) /* *
            Helper.scaleUpAndDown(phaseWidth, 0, 700)*/
      ),
    };
  },

  scaleUpAndDown: (actualWidth, min, max) => {
    return (
      Math.min(1, max / Helper.safeNumber(actualWidth)) *
      Math.max(1, min / Helper.safeNumber(actualWidth))
    );
  },
  movePlayerByArrow: (pos, direction, full) => {
    switch (direction) {
      case "up": {
        return full
          ? [Math.round(pos[0] + 1), pos[1]]
          : [pos[0], Math.round(pos[1] - 1)];
      }
      case "down": {
        return full
          ? [Math.round(pos[0] - 1), pos[1]]
          : [pos[0], Math.round(pos[1] + 1)];
      }
      case "left": {
        return full
          ? [pos[0], Math.round(pos[1] - 1)]
          : [Math.round(pos[0] - 1), pos[1]];
      }
      case "right": {
        return full
          ? [pos[0], Math.round(pos[1] + 1)]
          : [Math.round(pos[0] + 1), pos[1]];
      }
      default: {
        return pos;
      }
    }
  },
  getAllPlaysFromPlaybook: (chapters, uniqueCol = "play_id") => {
    if (!chapters) {
      return [];
    }

    let tempPlays = chapters.reduce(
      (acc, curr) => [
        ...acc,
        ...curr.objects.filter(
          (play) => play.type === "play" && !play?.data?.data?.error
        ),
      ],
      []
    );

    return tempPlays.filter((value, index, self) => {
      return self.findIndex((v) => v[uniqueCol] === value[uniqueCol]) === index;
    });
  },
  addUuidToObjects: (chapters, uuid) => {
    return chapters.map((chapter) => ({
      ...chapter,
      objects: chapter.objects.map((object) => ({
        ...object,
        uuid: uuid(),
      })),
    }));
  },
  getAllPlayIDs: (chapters) => {
    if (!chapters) {
      return [];
    }

    return chapters.reduce(
      (acc, curr) => [
        ...acc,
        ...curr.objects
          .filter((play) => play.type === "play")
          .map((play) => play.play_id),
      ],
      []
    );
  },

  getNextUrl: (filterObject, urlMapping, urlBase = "") => {
    let tempQueryString = queryString.stringify(filterObject);
    let find = urlMapping.find((x) => x.query === tempQueryString);

    if (find) {
      return find.url;
    } else {
      return urlBase + (tempQueryString === "" ? "" : "?" + tempQueryString);
    }
  },

  easeOutQuint: (t) => {
    return 1 + --t * t * t * t * t;
  },

  smoothScrollToIndex: (
    virtualizer,
    scrollingRef,
    index,
    { align: initialalign, duration = 1000, scrollPadding = 80 } = {}
  ) => {
    const direction =
      virtualizer.getOffsetForIndex(index, initialalign)?.[0] -
      virtualizer.scrollOffset;

    const start =
      virtualizer.getOffsetForIndex(index, initialalign)?.[0] +
      (direction > 0 ? -200 : 200) -
      scrollPadding;

    const startTime = (scrollingRef.current = Date.now());
    const [, align] = virtualizer.getOffsetForIndex(index, initialalign);

    const run = () => {
      if (scrollingRef.current !== startTime) return;
      const now = Date.now();
      const elapsed = now - startTime;
      const progress = Helper.easeOutQuint(Math.min(elapsed / duration, 1));
      const offset =
        virtualizer.getOffsetForIndex(index, align)[0] - scrollPadding;
      const interpolated = start + (offset - start) * progress;

      if (elapsed < duration) {
        virtualizer.scrollToOffset(interpolated, { align: "start" });
        requestAnimationFrame(run);
      } else {
        virtualizer.scrollToOffset(interpolated, { align: "start" });
      }
    };

    requestAnimationFrame(run);
  },
};
export default Helper;
